import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Bell, Users, Calendar, Edit2, Trash2, Send, Search, Filter, X, ChevronRight, CheckCircle, AlertCircle, Clock, Info, Check, ChevronDown, Monitor, Mail, MessageSquare, RefreshCw, Target } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSocket } from '../context/SocketContext';
import {
  fetchNotifications,
  fetchNotificationStats,
  fetchEventTypes,
  fetchTahsilatGroups,
  createNotification,
  updateNotification,
  deleteNotification,
  sendNotification,
  updateNotificationStatus,
  clearError,
  updateNotificationFromWebSocket,
  // Phase 1.1: Optimistic Updates
  addOptimisticNotification,
  commitOptimisticUpdate,
  rollbackOptimisticUpdate,
  NOTIFICATIONS_PAGE_SIZE,
} from '../redux/slices/notificationSlice';
import { api, BACKEND_URL } from '../redux/api/axiosManager';
import { ModalReporter } from '../context/ModalContext';
import { LimelightNav } from '../components/ui/limelight-nav';
import { Dropdown } from '../components/ui/dropdown-01';
import { NumberedPagination } from '../components/ui/numbered-pagination';
import {
  getNotificationDeleteInfo,
  parseNotificationDeleteError,
} from '../utils/notificationDeleteRules';
import { PERMISSIONS, usePermissionHook, PERMISSION_DENIED_MESSAGE, permissionGuardClass } from '../utils/permissions';

function normalizeNotificationData(data) {
  if (!data) return {};
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return typeof data === 'object' && !Array.isArray(data) ? data : {};
}

function stableStringify(value) {
  if (value == null || typeof value !== 'object') return JSON.stringify(value ?? {});
  const sorted = Object.keys(value)
    .sort()
    .reduce((acc, key) => {
      acc[key] = value[key];
      return acc;
    }, {});
  return JSON.stringify(sorted);
}

/** Kayıtlı bildirimin hangi senaryo preset'ine ait olduğunu çöz */
function getNotificationDataField(data, key) {
  const aliases = {
    mode: ['mode', 'reminderMode', 'reminder_mode'],
    targetDays: ['targetDays', 'target_days', 'days', 'overdueDays', 'overdue_days'],
    days: ['days', 'minIdleDays', 'min_idle_days'],
    maxDays: ['maxDays', 'max_days'],
    minIdleDays: ['minIdleDays', 'min_idle_days', 'days'],
  };
  const candidates = aliases[key] || [
    key,
    key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`),
  ];
  for (const candidate of candidates) {
    if (data[candidate] != null && data[candidate] !== '') {
      return data[candidate];
    }
  }
  return undefined;
}

function isEventPresetActive(preset, formData) {
  const data = normalizeNotificationData(formData?.data);
  const savedKey = data.presetKey || data.scenarioKey || data.scenario;
  if (savedKey && savedKey === preset.key) return true;

  const presetData = preset.data || {};
  if (Object.keys(presetData).length > 0) {
    const dataFieldsMatch = Object.entries(presetData).every(([key, expected]) => {
      const actual = getNotificationDataField(data, key);
      if (actual == null) return false;
      if (typeof expected === 'number' || typeof actual === 'number') {
        return Number(actual) === Number(expected);
      }
      return String(actual) === String(expected);
    });
    if (dataFieldsMatch) return true;
  }

  return (
    formData?.title === preset.title &&
    formData?.body === preset.body &&
    stableStringify(data) === stableStringify(presetData)
  );
}

function applyEventPreset(prev, preset) {
  return {
    ...prev,
    title: preset.title,
    body: preset.body,
    data: {
      ...(preset.data || {}),
      presetKey: preset.key,
    },
    recurringTime: prev.recurringTime || '19:00',
  };
}

// Phase 1.2: Error Handling - Error Types
const ERROR_TYPES = {
  NETWORK_ERROR: 'Bağlantı hatası. Tekrar deneyin.',
  VALIDATION_ERROR: 'Lütfen formu kontrol edin.',
  SERVER_ERROR: 'Sunucu hatası. Daha sonra tekrar deneyin.',
  PERMISSION_ERROR: 'Bu işlem için yetkiniz yok.',
  RATE_LIMIT_ERROR: 'Çok fazla istek. Lütfen bekleyin.',
  UNKNOWN_ERROR: 'Bir hata oluştu. Tekrar deneyin.'
};

// Error classification
const classifyError = (error) => {
  if (!error) return 'UNKNOWN_ERROR';
  if (error.response) {
    const status = error.response.status;
    if (status === 401 || status === 403) return 'PERMISSION_ERROR';
    if (status === 429) return 'RATE_LIMIT_ERROR';
    if (status >= 500) return 'SERVER_ERROR';
    if (status === 400 || status === 422) return 'VALIDATION_ERROR';
  }
  if (error.message?.includes('Network') || error.message?.includes('network')) return 'NETWORK_ERROR';
  return 'UNKNOWN_ERROR';
};

// Phase 2.3: Debounce helper
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Mağaza listesi (office codes)
const STORE_LOCATIONS = [
  { code: 'Y342', name: 'Bakırköy Yenikoza By Ender' },
  { code: 'Y261', name: 'Eskişehir Yenikoza By Ender' },
  { code: 'Y332', name: 'Mersin Yenikoza By Ender' },
  { code: 'Y421', name: 'Konya Yenikoza By Ender' },
  { code: 'Y013', name: 'Adana Yenikoza By Ender' },
];

// Helper: Haftanın günü numarasını isme çevir
const getDayOfWeekName = (dayOfWeek) => {
  const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  if (dayOfWeek === null || dayOfWeek === undefined) {
    return 'Pazartesi'; // Varsayılan
  }
  return days[dayOfWeek] || 'Pazartesi';
};

// Helper: Ayın günü bilgisini formatla
const formatDayOfMonth = (dayOfMonth) => {
  if (dayOfMonth === null || dayOfMonth === undefined) {
    return "Ayın 1'i"; // Varsayılan
  }
  if (dayOfMonth === -1) {
    return 'Ayın Son Günü';
  }
  return `Ayın ${dayOfMonth}'i`;
};

// Helper: Tekrarlayan bildirim bilgisini formatla
const formatRecurringInfo = (notification) => {
  if (!notification.recurringType || !notification.recurringTime) {
    return null;
  }

  const time = notification.recurringTime;

  switch (notification.recurringType) {
    case 'daily':
      return `Her Gün ${time}`;

    case 'weekly':
      const dayName = getDayOfWeekName(notification.recurringDayOfWeek);
      return `Her ${dayName} ${time}`;

    case 'monthly':
      const dayInfo = formatDayOfMonth(notification.recurringDayOfMonth);
      return `Her ${dayInfo} ${time}`;

    default:
      return null;
  }
};

// Helper: Tarih dönüştürücü (görüntüleme için)
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // .NET formatı kontrolü
      const timestamp = dateString.match(/\d+/)?.[0];
      if (timestamp) {
        const dotNetDate = new Date(parseInt(timestamp));
        const day = String(dotNetDate.getDate()).padStart(2, '0');
        const month = String(dotNetDate.getMonth() + 1).padStart(2, '0');
        const year = dotNetDate.getFullYear();
        return `${day}.${month}.${year}`;
      }
      return '-';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (error) {
    // Hata sessizce yok sayılıyor
    return '-';
  }
};

// Helper: Tek seferlik zamanlanmış bildirim bilgisini formatla (tarih + saat)
const formatScheduledTime = (notification) => {
  // Tek seferlik zamanlanmış bildirim: scheduledTime var ama recurringType yok
  if (!notification.scheduledTime || notification.recurringType) {
    return null;
  }

  try {
    const scheduledDate = parseScheduledTime(notification.scheduledTime);
    if (!scheduledDate || isNaN(scheduledDate.getTime())) {
      return null;
    }

    // Tarih ve saat formatla: "03.12.2025 15:30" formatında
    const day = String(scheduledDate.getDate()).padStart(2, '0');
    const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
    const year = scheduledDate.getFullYear();
    const hours = String(scheduledDate.getHours()).padStart(2, '0');
    const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch (error) {
    // Hata sessizce yok sayılıyor
    return null;
  }
};

// Helper: Tek seferlik zamanlanmış bildirim için sadece saat formatla (HH:mm)
const formatScheduledTimeOnly = (notification) => {
  if (!notification.scheduledTime || notification.recurringType) {
    return null;
  }

  try {
    const scheduledDate = parseScheduledTime(notification.scheduledTime);
    if (!scheduledDate || isNaN(scheduledDate.getTime())) {
      return null;
    }

    const hours = String(scheduledDate.getHours()).padStart(2, '0');
    const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (error) {
    return null;
  }
};

// Helper: Backend'den gelen scheduledTime formatını Date objesine çevir
const parseScheduledTime = (scheduledTime) => {
  if (!scheduledTime || scheduledTime === null || scheduledTime === '') {
    return null;
  }

  try {
    // Microsoft JSON date formatını kontrol et: "/Date(1764763200000)/"
    const dateMatch = String(scheduledTime).match(/\/Date\((\d+)\)\//);
    if (dateMatch) {
      const timestamp = parseInt(dateMatch[1]);
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // ISO string formatı: "2025-12-03T15:00:00.000Z" veya "2025-12-03 15:00:00"
    if (typeof scheduledTime === 'string') {
      // "YYYY-MM-DD HH:mm:ss" formatını ISO'ya çevir
      const dateTimeMatch = scheduledTime.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
      if (dateTimeMatch) {
        const [, year, month, day, hours, minutes, seconds] = dateTimeMatch;
        const date = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      // ISO string formatı
      const date = new Date(scheduledTime);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Number (timestamp) ise
    if (typeof scheduledTime === 'number') {
      const date = new Date(scheduledTime);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    return null;
  } catch (error) {
    // Hata sessizce yok sayılıyor
    return null;
  }
};

const NotificationManagement = () => {
  const dispatch = useDispatch();
  const {
    notifications,
    stats,
    pagination,
    filters,
    loading,
    statsLoading,
    error,
    eventTypes,
    eventTypesLoading,
    tahsilatGroups = [],
  } = useSelector((state) => state.notifications);

  const canManageNotifications = usePermissionHook(PERMISSIONS.NOTIFICATIONS_MANAGE);

  const requireManagePermission = useCallback(() => {
    if (!canManageNotifications) {
      toast.error(PERMISSION_DENIED_MESSAGE);
      return false;
    }
    return true;
  }, [canManageNotifications]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, notification: null, deleteInfo: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  // localStorage'dan aktif sekmeyi yükle, yoksa varsayılan olarak 'immediate'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('notificationManagement_activeTab');
    return savedTab || 'immediate'; // 'immediate', 'scheduled', 'recurring', 'event_based'
  });


  const refreshTimeoutRef = useRef(null);
  const latestFiltersRef = useRef({
    activeTab,
    filterType,
    filterStatus,
    searchTerm,
    pagination,
    showCreateModal,
    showEditModal,
  });
  const lastScheduledSnapshotRef = useRef(new Set());
  const csvFileInputRef = useRef(null);
  /** Son CSV (liste seçimi veya yükleme) ile eklenen kullanıcı ID'leri — seçim kaldırılınca formdan silinir */
  const csvSelectionUserIdsRef = useRef([]);

  // Form state
  const rawUploadBaseUrl =
    process.env.REACT_APP_NOTIFICATION_UPLOAD_BASE_URL ||
    process.env.NOTIFICATION_UPLOAD_BASE_URL ||
    process.env.REACT_APP_PUBLIC_BASE_URL ||
    process.env.PUBLIC_BASE_URL ||
    BACKEND_URL ||
    '';

  const NOTIFICATION_UPLOAD_BASE_URL = rawUploadBaseUrl.trim().replace(/\/$/, '');

  const resolvePreviewUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    if (/^https?:\/\//i.test(url)) return url;
    const baseUrl = NOTIFICATION_UPLOAD_BASE_URL;
    if (baseUrl) {
      if (url.startsWith('/')) {
        return `${baseUrl}${url}`;
      }
      return `${baseUrl}/${url}`;
    }
    if (typeof window !== 'undefined') {
      if (url.startsWith('/')) {
        return `${window.location.origin}${url}`;
      }
      return `${window.location.origin}/${url}`;
    }
    return url;
  };

  const [formData, setFormData] = useState({
    type: 'push',
    title: '',
    body: '',
    userIds: [],
    sendTo: 'all',
    priority: 'normal',
    channels: ['push', 'websocket'], // Varsayılan: Her ikisi de seçili
    imageUrl: '',
    data: {},
    promotionData: null,
    sendType: 'immediate', // 'immediate', 'scheduled', 'recurring', 'event_based'
    scheduledTime: null,
    recurringType: null, // 'daily', 'weekly', 'monthly'
    recurringTime: null, // 'HH:mm' formatında saat
    recurringDayOfWeek: null, // 0-6 arası (0=Pazar, 1=Pazartesi, ..., 6=Cumartesi) - Sadece weekly için
    recurringDayOfMonth: null, // 1-31 arası (ayın günü) veya -1 (ayın son günü) - Sadece monthly için
    eventType: null, // 'user_registered', 'first_order', 'order_confirmed', etc.
    officeCodes: [], // Mağaza filtreleme için seçilen office kodları
    tahsilatGroups: [], // Tahsilat kategorileri (sendTo === 'tahsilat' iken)
  });
  const [imagePreview, setImagePreview] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedUserInputValue, setSelectedUserInputValue] = useState(''); // Chip input için yazılan TC/ID
  const [recipientInputMode, setRecipientInputMode] = useState('manual'); // 'manual' | 'csv'
  const [csvFile, setCsvFile] = useState(null);
  const [csvName, setCsvName] = useState('');
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  const [recipientBatchId, setRecipientBatchId] = useState('');
  const [imports, setImports] = useState([]);
  const [importsLoading, setImportsLoading] = useState(false);
  const [importsQuery, setImportsQuery] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editingImportId, setEditingImportId] = useState('');
  const [editingImportName, setEditingImportName] = useState('');
  const [selectedImportSummary, setSelectedImportSummary] = useState(null);
  const [highlightScheduledTime, setHighlightScheduledTime] = useState(false);
  const [duplicateCsvWarning, setDuplicateCsvWarning] = useState({
    open: false,
    name: '',
    existing: null,
  });

  // Herhangi bir modal açıkken arka plan (body) scroll'unu kilitle — sağ scrollbar
  // şeridi ve arka plan kayması nedeniyle oluşan beyaz alanları engeller.
  const isAnyModalOpen =
    showCreateModal || showEditModal || deleteConfirm.show || duplicateCsvWarning.open;
  useEffect(() => {
    if (!isAnyModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAnyModalOpen]);

  const inactiveSinceFirstOpenPresets = [
    {
      key: '3-9',
      label: '3 Gün Sonra',
      title: 'Sana Uygun Fırsatlar Hazır 🎉🛍️',
      body: "Kozakart'ta seni bekleyen fırsatları keşfetmek için hemen tıkla, kampanyalara göz at.",
      data: {
        days: 3,
        maxDays: 10,
        minIdleDays: 3,
      },
    },
    {
      key: '10+',
      label: '10 Gün Sonra',
      title: 'Sana Özel Avantajlar Hazır 🎉👀',
      body: "Kozakart'ta kullanabileceğin kampanyalar ve avantajlar seni bekliyor.",
      data: {
        days: 10,
        minIdleDays: 10,
      },
    },
  ];

  const paymentReminderPresets = [
    {
      key: 'upcoming_5',
      label: '5 Gün Kala',
      title: 'Ödemenizi Unutmayın ⏰',
      body: 'Taksitinizin son ödeme tarihine 5 gün kaldı. Gecikme yaşamamak için şimdi kontrol edin.',
      data: {
        mode: 'upcoming_exact',
        targetDays: 5,
      },
    },
    {
      key: 'due_today',
      label: 'Vade Günü',
      title: 'Son Ödeme Tarihi Bugün 📌',
      body: 'Taksitinizin son ödeme tarihi bugün. Gecikmeye düşmemek için ödemenizi tamamlayın.',
      data: {
        mode: 'due_today',
        targetDays: 0,
      },
    },
    {
      key: 'overdue_3',
      label: '3 Gün Gecikmiş',
      title: 'Ödemeniz Gecikmiş Görünüyor ⚠️',
      body: 'Son ödeme tarihiniz 3 gün geçmiş görünüyor. Gecikme artmadan ödemenizi yapın.',
      data: {
        mode: 'overdue_exact',
        targetDays: 3,
      },
    },
    {
      key: 'overdue_15',
      label: '15 Gün Gecikmiş',
      title: 'Ödemeniz Uzun Süredir Gecikmede 🚨',
      body: 'Son ödeme tarihiniz 15 gün geçmiş durumda. Ek maliyet oluşmaması için lütfen hemen ödeme yapın.',
      data: {
        mode: 'overdue_exact',
        targetDays: 15,
      },
    },
  ];

  // Form channels (push, websocket) → API format: Hibrit=["push"], Sadece push=["push_only"], Sadece WebSocket=["websocket_only"]
  const formChannelsToApi = useCallback((formChannels) => {
    if (!formChannels || formChannels.length === 0) return ['push'];
    const hasPush = formChannels.includes('push');
    const hasWebsocket = formChannels.includes('websocket');
    const others = formChannels.filter(c => !['push', 'websocket'].includes(c));
    let pushWebsocketPart = [];
    if (hasPush && hasWebsocket) {
      pushWebsocketPart = ['push']; // Hibrit
    } else if (hasPush) {
      pushWebsocketPart = ['push_only'];
    } else if (hasWebsocket) {
      pushWebsocketPart = ['websocket_only'];
    } else {
      pushWebsocketPart = ['push']; // Varsayılan
    }
    return [...pushWebsocketPart, ...others];
  }, []);

  // API channels → Form format (düzenleme için)
  const apiChannelsToForm = useCallback((apiChannels) => {
    if (!apiChannels || apiChannels.length === 0) return ['push', 'websocket'];
    const hasPush = apiChannels.includes('push');
    const hasPushOnly = apiChannels.includes('push_only');
    const hasWebsocketOnly = apiChannels.includes('websocket_only');
    const others = apiChannels.filter(c => !['push', 'push_only', 'websocket', 'websocket_only'].includes(c));
    let pushWebsocketPart = [];
    if (hasPush) {
      pushWebsocketPart = ['push', 'websocket'];
    } else if (hasPushOnly) {
      pushWebsocketPart = ['push'];
    } else if (hasWebsocketOnly) {
      pushWebsocketPart = ['websocket'];
    } else {
      pushWebsocketPart = ['push', 'websocket'];
    }
    return [...new Set([...pushWebsocketPart, ...others])];
  }, []);

  // Helper: activeTab'i kategoriye çevir
  const getCategoryFromTab = useCallback((tab) => {
    switch (tab) {
      case 'immediate':
        return 'instant';
      case 'recurring':
        return 'recurring';
      case 'event_based':
        return 'event-based';
      default:
        return 'instant';
    }
  }, []);

  useEffect(() => {
    latestFiltersRef.current = {
      activeTab,
      filterType,
      filterStartDate,
      filterEndDate,
      searchTerm,
      pagination,
      showCreateModal,
      showEditModal,
    };
  }, [activeTab, filterType, filterStatus, filterStartDate, filterEndDate, searchTerm, pagination, showCreateModal, showEditModal]);

  // activeTab değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('notificationManagement_activeTab', activeTab);
  }, [activeTab]);

  // scheduleRealtimeRefresh fonksiyonunu önce tanımla (useEffect'ten önce)
  const scheduleRealtimeRefresh = useCallback(
    (reason = 'socket-event', detail) => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = setTimeout(() => {
        const {
          activeTab: latestActiveTab,
          filterType: latestFilterType,
          filterStatus: latestFilterStatus,
          filterStartDate: latestFilterStartDate,
          filterEndDate: latestFilterEndDate,
          searchTerm: latestSearchTerm,
          pagination: latestPagination,
          showCreateModal: latestShowCreateModal,
          showEditModal: latestShowEditModal,
        } = latestFiltersRef.current;

        if (latestShowCreateModal || latestShowEditModal) {
          return;
        }

        const category = getCategoryFromTab(latestActiveTab);
        const isTC = /^\d{11}$/.test(latestSearchTerm);

        dispatch(
          fetchNotifications({
            category,
            type: latestFilterType || undefined,
            status: latestFilterStatus || undefined,
            startDate: latestFilterStartDate || undefined,
            endDate: latestFilterEndDate || undefined,
            customerId: isTC ? latestSearchTerm : undefined,
            search: !isTC ? latestSearchTerm : undefined,
            page: latestPagination?.page || 1,
            limit: latestPagination?.limit || NOTIFICATIONS_PAGE_SIZE,
            merge: false,
            force: true, // WebSocket event'inden sonra cache'i bypass et
          })
        );

        dispatch(fetchNotificationStats({ force: true }));
      }, 350);
    },
    [dispatch, getCategoryFromTab]
  );

  // WebSocket Entegrasyonu
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) {
      return;
    }

    // Event handler'ları tanımla (useEffect scope'unda)
    // Backend: notification-created
    const handleNotificationCreated = (data) => {
      // Hemen liste ve stats'ı güncelle (cache bypass ile)
      // latestFiltersRef'ten güncel değerleri al
      const {
        activeTab: latestActiveTab,
        filterType: latestFilterType,
        filterStatus: latestFilterStatus,
        filterStartDate: latestFilterStartDate,
        filterEndDate: latestFilterEndDate,
        searchTerm: latestSearchTerm,
        pagination: latestPagination,
      } = latestFiltersRef.current;
      
      const currentCategory = getCategoryFromTab(latestActiveTab);
      const isTC = /^\d{11}$/.test(latestSearchTerm);
      
      dispatch(fetchNotifications({
        category: currentCategory,
        type: latestFilterType || undefined,
        status: latestFilterStatus || undefined,
        startDate: latestFilterStartDate ? new Date(latestFilterStartDate).toISOString() : undefined,
        endDate: latestFilterEndDate ? new Date(latestFilterEndDate).toISOString() : undefined,
        customerId: isTC ? latestSearchTerm : undefined,
        search: !isTC ? latestSearchTerm : undefined,
          page: latestPagination?.page || 1,
          limit: latestPagination?.limit || NOTIFICATIONS_PAGE_SIZE,
          merge: false,
          force: true, // Cache'i bypass et
      }));
      dispatch(fetchNotificationStats({ force: true }));
    };

    // Backend: notification-updated
    const handleNotificationUpdated = (data) => {
      // Yeni format: { notification: {...}, category, timestamp }
      // Eski format: { notificationId, updates, status, ... }
      const notification = data.notification || {};
      const notificationId = notification.id || data.notificationId;
      const status = notification.status || data.status || data.updates?.status;
      const sendCount = notification.sendCount !== undefined ? notification.sendCount : (data.sendCount !== undefined ? data.sendCount : data.updates?.sendCount);
      const successCount = notification.successCount !== undefined ? notification.successCount : (data.successCount !== undefined ? data.successCount : data.updates?.successCount);
      const failedCount = notification.failedCount !== undefined ? notification.failedCount : (data.failedCount !== undefined ? data.failedCount : data.updates?.failedCount);
      
      // Bildirim listesindeki ilgili bildirimi anlık güncelle
      if (notificationId) {
        const updatePayload = {
          status: status,
          sendCount: sendCount,
          sentCount: sendCount, // sendCount'u sentCount olarak da kullan
          successCount: successCount,
          failedCount: failedCount,
        };
        
        dispatch(updateNotificationFromWebSocket({
          notificationId: notificationId,
          updates: updatePayload,
          timestamp: data.timestamp || notification.updatedAt
        }));
      }
      
      // Stats'ı da güncelle
      dispatch(fetchNotificationStats({ force: true }));
    };

    // Backend: notification-sent
    const handleNotificationSent = (data) => {
      // Yeni format: { notification: {...}, category, sentAt, timestamp }
      // Eski format: { notificationId, status, sendCount, ... }
      const notification = data.notification || {};
      const notificationId = notification.id || data.notificationId;
      const status = notification.status || data.status || 'sent';
      const sendCount = notification.sendCount !== undefined ? notification.sendCount : (data.sendCount !== undefined ? data.sendCount : undefined);
      const successCount = notification.successCount !== undefined ? notification.successCount : (data.successCount !== undefined ? data.successCount : undefined);
      const failedCount = notification.failedCount !== undefined ? notification.failedCount : (data.failedCount !== undefined ? data.failedCount : undefined);
      const sentAt = data.sentAt || notification.sentAt || notification.updatedAt;
      
      // Bildirim listesindeki ilgili bildirimi anlık güncelle
      if (notificationId) {
        const updatePayload = {
          status: status,
          sendCount: sendCount,
          sentCount: sendCount, // sendCount'u sentCount olarak da kullan
          successCount: successCount,
          failedCount: failedCount,
        };
        
        dispatch(updateNotificationFromWebSocket({
          notificationId: notificationId,
          updates: updatePayload,
          timestamp: data.timestamp || sentAt
        }));
      }
      
      // Stats'ı da güncelle
      dispatch(fetchNotificationStats({ force: true }));
      
      // NOT: Liste yenileme yapmıyoruz çünkü WebSocket event'i zaten Redux state'ini güncelliyor
      // notification-sent event'i final durumu gösteriyor, backend'den eski veri çekmek güncel veriyi override edebilir
    };

    // Backend: notification-deleted
    const handleNotificationDeleted = (data) => {
      scheduleRealtimeRefresh('socket-event', 'notification-deleted');
    };

    // Backend: notification-scheduled
    const handleNotificationScheduled = (data) => {
      scheduleRealtimeRefresh('socket-event', 'notification-scheduled');
    };

    // Backend: notification-status-updated
    const handleNotificationStatusUpdated = (data) => {
      // Bildirim listesindeki ilgili bildirimi anlık güncelle
      if (data.notificationId && data.newStatus) {
        dispatch(updateNotificationFromWebSocket({
          notificationId: data.notificationId,
          updates: {
            status: data.newStatus,
          }
        }));
      }
      
      scheduleRealtimeRefresh('socket-event', 'notification-status-updated');
    };

    // Socket bağlantı event'lerini de dinle
    const handleConnect = () => {};

    const handleDisconnect = () => {};

    const handleConnectError = () => {};

    // Event listener'ları kaydetme fonksiyonu
    const registerEventListeners = () => {
      socket.on('notification-created', handleNotificationCreated);
      socket.on('notification-updated', handleNotificationUpdated);
      socket.on('notification-sent', handleNotificationSent);
      socket.on('notification-deleted', handleNotificationDeleted);
      socket.on('notification-scheduled', handleNotificationScheduled);
      socket.on('notification-status-updated', handleNotificationStatusUpdated);

      // Eski event isimlerini de dinle (geriye dönük uyumluluk için)
      socket.on('notification:new', handleNotificationCreated);
      socket.on('notification:update', handleNotificationUpdated);
      socket.on('notification:sent', handleNotificationSent);
      
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('connect_error', handleConnectError);
    };

    if (!socket.connected) {
      // Socket bağlanana kadar bekle
      const connectHandler = () => {
        registerEventListeners();
      };
      socket.once('connect', connectHandler);
      return () => {
        socket.off('connect', connectHandler);
        // Cleanup
        if (socket) {
          socket.off('notification-created', handleNotificationCreated);
          socket.off('notification-updated', handleNotificationUpdated);
          socket.off('notification-sent', handleNotificationSent);
          socket.off('notification-deleted', handleNotificationDeleted);
          socket.off('notification-scheduled', handleNotificationScheduled);
          socket.off('notification-status-updated', handleNotificationStatusUpdated);
          socket.off('notification:new', handleNotificationCreated);
          socket.off('notification:update', handleNotificationUpdated);
          socket.off('notification:sent', handleNotificationSent);
          socket.off('connect', handleConnect);
          socket.off('disconnect', handleDisconnect);
          socket.off('connect_error', handleConnectError);
        }
      };
    }

    // Socket bağlıysa direkt kayıt et
    registerEventListeners();

    // Cleanup function
    return () => {
      if (socket) {
        socket.off('notification-created', handleNotificationCreated);
        socket.off('notification-updated', handleNotificationUpdated);
        socket.off('notification-sent', handleNotificationSent);
        socket.off('notification-deleted', handleNotificationDeleted);
        socket.off('notification-scheduled', handleNotificationScheduled);
        socket.off('notification-status-updated', handleNotificationStatusUpdated);
        
        // Eski event isimlerini de temizle
        socket.off('notification:new', handleNotificationCreated);
        socket.off('notification:update', handleNotificationUpdated);
        socket.off('notification:sent', handleNotificationSent);
        
        // Socket bağlantı event'lerini de temizle
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('connect_error', handleConnectError);
      }
    };
  }, [socket, dispatch, getCategoryFromTab]);

  // Sayfa yüklendiğinde verileri çek
  useEffect(() => {
    const category = getCategoryFromTab(activeTab);
    dispatch(fetchNotifications({ category, page: 1, limit: NOTIFICATIONS_PAGE_SIZE, merge: false }));
    dispatch(fetchNotificationStats());
    // Event types ve tahsilat gruplarını çek (sadece bir kez)
    if (eventTypes.length === 0 && !eventTypesLoading) {
      dispatch(fetchEventTypes());
    }
    if (tahsilatGroups.length === 0) {
      dispatch(fetchTahsilatGroups());
    }
  }, [dispatch, activeTab, eventTypes.length, eventTypesLoading, tahsilatGroups.length]);

  // Phase 2.3: Debounced Search & Filters
  const debouncedFetchNotifications = useMemo(
    () => debounce((params) => {
      if (showCreateModal || showEditModal) {
        return;
      }
      dispatch(fetchNotifications(params));
    }, 300),
    [dispatch, showCreateModal, showEditModal]
  );

  // Filtreler değiştiğinde bildirimleri yeniden çek (debounced)
  useEffect(() => {
    const category = getCategoryFromTab(activeTab);
    const isTC = /^\d{11}$/.test(searchTerm);
    
    debouncedFetchNotifications({
      category,
      type: filterType || undefined,
      status: filterStatus || undefined,
      startDate: filterStartDate ? new Date(filterStartDate).toISOString() : undefined,
      endDate: filterEndDate ? new Date(filterEndDate).toISOString() : undefined,
      customerId: isTC ? searchTerm : undefined,
      search: !isTC ? searchTerm : undefined,
      page: 1, // Filtreler değiştiğinde her zaman 1. sayfaya dön
      limit: pagination.limit,
      merge: false,
      force: false, // Cache kontrolüne izin ver
    });
  }, [
    activeTab,
    searchTerm,
    filterType,
    filterStatus,
    filterStartDate,
    filterEndDate,
    pagination.limit,
    debouncedFetchNotifications,
    getCategoryFromTab,
  ]);

  // Hata durumunda toast göster
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Zamanlanmış bildirimleri kontrol et ve zamanı gelmiş olanları gönder
  useEffect(() => {
    if (showCreateModal || showEditModal) {
      return undefined;
    }

    let isChecking = false; // Çoklu gönderimi önlemek için flag

    const checkScheduledNotifications = async () => {
      // Eğer zaten kontrol yapılıyorsa bekle
      if (isChecking) {
        return;
      }

      isChecking = true;

      try {
        // Tüm kategorilerden zamanlanmış bildirimleri state'i güncellemeden çek
        // Not: Event-based bildirimler otomatik gönderilir, manuel kontrol gereksiz
        const now = new Date();
        const allNotifications = [];

        // API'ye direkt istek at (state'i güncelleme) - Artık tek endpoint kullanılıyor
        try {
          const response = await api.get(
            `/api/admin/dashboard/notifications?status=scheduled&page=1&limit=${NOTIFICATIONS_PAGE_SIZE}`
          );
          if (response.data?.success && response.data?.data) {
            const mapped = response.data.data.map(notification => ({
              id: notification.id || notification._id,
              status: notification.status || 'draft',
              scheduledTime: notification.scheduledTime || null, // Backend'den gelen format (parse edilecek)
              sentAt: notification.sentAt || null,
              title: notification.title || '',
              eventType: notification.eventType || null,
              recurringType: notification.recurringType || null,
            }));
            allNotifications.push(...mapped);
          }
        } catch (error) {
          // Hata sessizce yok sayılıyor
        }

        // Scheduled snapshot'ı kontrol et (backend otomatik gönderimlerini yakalamak için)
        const currentScheduledSnapshot = new Set(allNotifications.map((notification) => notification.id));
        const previousScheduledSnapshot = lastScheduledSnapshotRef.current;
        if (previousScheduledSnapshot.size > 0 && currentScheduledSnapshot.size === 0) {
          scheduleRealtimeRefresh('scheduled-check-empty');
        }
        lastScheduledSnapshotRef.current = currentScheduledSnapshot;

        // Zamanı gelmiş bildirimleri filtrele
        const scheduledNotifications = allNotifications.filter(
          (notification) => {
            const hasScheduledTime = notification.scheduledTime &&
              notification.scheduledTime !== null &&
              notification.scheduledTime !== '';

            // scheduledTime formatını parse et (helper fonksiyon kullan)
            const scheduledDate = hasScheduledTime ? parseScheduledTime(notification.scheduledTime) : null;

            const isTimeReached = scheduledDate && !isNaN(scheduledDate.getTime()) && scheduledDate <= now;

            // Sadece 'scheduled' status'lu ve henüz gönderilmemiş bildirimleri gönder
            // 'sent', 'failed', 'cancelled' status'lu bildirimleri gönderme
            const shouldSend = notification.status === 'scheduled' &&
              hasScheduledTime &&
              isTimeReached &&
              !notification.sentAt && // Henüz gönderilmemiş olanlar
              notification.status !== 'sent' && // Zaten gönderilmiş olanları gönderme
              notification.status !== 'failed' && // Başarısız olanları tekrar gönderme
              notification.status !== 'cancelled'; // İptal edilmiş olanları gönderme

            return shouldSend;
          }
        );

        if (scheduledNotifications.length > 0) {
          // Her bildirimi sırayla gönder
          for (const notification of scheduledNotifications) {
            try {
              const notificationCategory = notification.eventType
                ? 'event-based'
                : (notification.recurringType &&
                  (notification.recurringType === 'daily' ||
                    notification.recurringType === 'weekly' ||
                    notification.recurringType === 'monthly'))
                  ? 'recurring'
                  : 'instant'; // Tek seferlik zamanlanmış (scheduledTime var ama recurringType yok) veya anlık bildirimler instant kategorisinde

              await dispatch(sendNotification({ id: notification.id, category: notificationCategory })).unwrap();

              toast.success(`Zamanlanmış bildirim gönderildi: ${notification.title}`);

              await dispatch(fetchNotificationStats({ force: true }));
            } catch (error) {
              // Eğer bildirim zaten gönderilmişse, sadece stats'ı güncelle (hata gösterme)
              const errorMessage = error?.response?.data?.message || error?.message || '';
              if (errorMessage.includes('zaten gönderilmiş') || errorMessage.includes('already sent')) {
                // NOT: Liste yenileme yapmıyoruz çünkü WebSocket event'leri zaten state'i güncelliyor
                dispatch(fetchNotificationStats({ force: true }));
              } else {
                toast.error(`Bildirim gönderilemedi: ${notification.title} - ${errorMessage || 'Bilinmeyen hata'}`);
              }
            }
          }

          dispatch(fetchNotificationStats({ force: true }));
        } else {
          const sentNotifications = allNotifications.filter((notification) => {
            const hasScheduledTime = notification.scheduledTime &&
              notification.scheduledTime !== null &&
              notification.scheduledTime !== '';
            const scheduledDate = hasScheduledTime ? new Date(notification.scheduledTime) : null;
            const isTimeReached = scheduledDate && scheduledDate <= now;

            return notification.status === 'sent' &&
              hasScheduledTime &&
              isTimeReached;
          });

          if (sentNotifications.length > 0) {
            const currentCategory = getCategoryFromTab(activeTab);

            dispatch(fetchNotifications({
              category: currentCategory,
              type: filterType || undefined,
              status: filterStatus || undefined,
              search: searchTerm || undefined,
              page: pagination.page,
              limit: pagination.limit,
              merge: false, // State'i tamamen yenile
            }));

            dispatch(fetchNotificationStats());
          }
        }
      } catch (error) {
        // Hata sessizce yok sayılıyor
      } finally {
        isChecking = false;
      }
    };

    // İlk kontrol (sayfa yüklendiğinde - 2 saniye bekle)
    const initialTimeout = setTimeout(() => {
      checkScheduledNotifications();
    }, 2000);

    const interval = setInterval(() => {
      checkScheduledNotifications();
    }, 30000); // 30 saniye (backend zaten otomatik gönderim yapıyor)

    // NOT: eventBasedStatsInterval kaldırıldı çünkü gereksiz API çağrılarına neden oluyordu
    // Stats güncellemesi zaten WebSocket event'lerinden ve bildirim gönderimlerinden sonra yapılıyor
    // Her 15 saniyede bir stats güncellemesi gereksiz ve performans sorununa neden oluyor

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [
    dispatch,
    filterType,
    filterStatus,
    searchTerm,
    pagination.page,
    pagination.limit,
    activeTab,
    showCreateModal,
    showEditModal,
  ]);

  // Durum etiketleri
  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Taslak',
      scheduled: 'Zamanlanmış',
      sending: 'Gönderiliyor',
      sent: 'Gönderildi',
      failed: 'Başarısız',
      cancelled: 'İptal Edildi',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-orange-100 text-orange-800',
      sending: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Tip etiketleri
  const getTypeLabel = (type) => {
    const labels = {
      push: 'Push',
      promotion: 'Promosyon',
      system: 'Sistem',
    };
    return labels[type] || type;
  };

  // Form resetle
  const resetForm = () => {
    setFormData({
      type: 'push',
      title: '',
      body: '',
      userIds: [],
      sendTo: 'all',
      priority: 'normal',
      channels: ['push', 'websocket'],
      imageUrl: '',
      data: {},
      promotionData: null,
      sendType: 'immediate',
      scheduledTime: null,
      recurringType: null,
      recurringTime: null,
      recurringDayOfWeek: null,
      recurringDayOfMonth: null,
      eventType: null,
      officeCodes: [],
      tahsilatGroups: [],
    });
    setEditingNotification(null);
    setImagePreview('');
    setImageUploading(false);
    setCsvFile(null);
    setCsvName('');
    setCsvImportLoading(false);
    setCsvImportResult(null);
    setRecipientBatchId('');
    setImportsQuery('');
    setIncludeArchived(false);
    setEditingImportId('');
    setEditingImportName('');
    setSelectedImportSummary(null);
    setDuplicateCsvWarning({ open: false, name: '', existing: null });
    setHighlightScheduledTime(false);
    setRecipientInputMode('manual');
    setSelectedUserInputValue('');
    csvSelectionUserIdsRef.current = [];
  };

  const fetchRecipientImports = useCallback(async (q = '', includeArchivedOverride) => {
    try {
      setImportsLoading(true);
      const archivedFlag = includeArchivedOverride ?? includeArchived;
      const response = await api.get('/api/admin/dashboard/notifications/recipients/imports', {
        params: { page: 1, limit: NOTIFICATIONS_PAGE_SIZE, q: q || undefined, includeArchived: archivedFlag },
        withCredentials: true,
      });
      const payload = response?.data?.data || response?.data || {};
      setImports(Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : []));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'CSV import listesi alınamadı');
    } finally {
      setImportsLoading(false);
    }
  }, [includeArchived]);

  const normalizeName = (value) => (value || '').toString().trim().toLocaleLowerCase('tr-TR');

  const getUploadTargetName = () => {
    if (csvName && csvName.trim()) return csvName.trim();
    if (csvFile?.name) return csvFile.name.replace(/\.csv$/i, '').trim();
    return '';
  };

  const findDuplicateImportByName = (targetName) => {
    const normalizedTarget = normalizeName(targetName);
    if (!normalizedTarget) return null;
    return (imports || []).find((item) => {
      const itemName = item?.name || (item?.fileName ? String(item.fileName).replace(/\.csv$/i, '') : '');
      return normalizeName(itemName) === normalizedTarget;
    }) || null;
  };

  const performCsvUpload = async (confirmedDuplicate = false) => {
    if (!csvFile) {
      toast.error('Lütfen bir CSV dosyası seçin');
      return;
    }

    if (!csvFile.name.toLowerCase().endsWith('.csv')) {
      toast.error('Sadece .csv dosyası yükleyebilirsiniz');
      return;
    }

    const targetName = getUploadTargetName();
    const existingDuplicate = findDuplicateImportByName(targetName);
    if (existingDuplicate && !confirmedDuplicate) {
      setDuplicateCsvWarning({
        open: true,
        name: targetName || existingDuplicate?.name || existingDuplicate?.fileName || 'CSV',
        existing: existingDuplicate,
      });
      return;
    }

    try {
      setCsvImportLoading(true);
      setCsvImportResult(null);

      const fd = new FormData();
      fd.append('file', csvFile);
      if (csvName.trim()) {
        fd.append('name', csvName.trim());
      }

      const response = await api.post('/api/admin/dashboard/notifications/recipients/import-csv', fd, {
        withCredentials: true,
      });

      const payload = response?.data?.data || response?.data || {};
      const importedBatchId = payload.batchId || '';
      const importedUserIds = Array.isArray(payload.validUserIds) ? payload.validUserIds : [];

      setRecipientBatchId(importedBatchId);
      setRecipientInputMode('csv');
      setSelectedImportSummary({
        batchId: importedBatchId,
        validCount: payload?.summary?.validCount ?? importedUserIds.length ?? 0,
        totalRows: payload?.summary?.totalRows ?? 0,
      });
      setCsvImportResult({
        summary: payload.summary || null,
        invalidRows: Array.isArray(payload.invalidRows) ? payload.invalidRows : [],
        errorFileUrl: payload.errorFileUrl || '',
      });

      csvSelectionUserIdsRef.current = importedUserIds.map(String);

      if (importedUserIds.length > 0) {
        setFormData((prev) => ({
          ...prev,
          userIds: [...new Set([...(Array.isArray(prev.userIds) ? prev.userIds : []), ...importedUserIds])],
        }));
      }

      setCsvName('');
      await fetchRecipientImports(importsQuery);
      if (confirmedDuplicate) {
        toast.success('CSV bilinçli tekrar yükleme ile kaydedildi.');
      } else {
        toast.success('CSV başarıyla işlendi');
      }
      setDuplicateCsvWarning({ open: false, name: '', existing: null });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'CSV yükleme hatası');
    } finally {
      setCsvImportLoading(false);
    }
  };

  const handleCsvUpload = () => performCsvUpload(false);

  /** Kayıtlı CSV seçimini, yüklenen CSV özetini veya seçili dosyayı kaldırır; bu CSV ile eklenen alıcıları listeden çıkarır */
  const clearCsvRecipientSelection = () => {
    const remove = new Set(csvSelectionUserIdsRef.current);
    if (remove.size > 0) {
      setFormData((prev) => ({
        ...prev,
        userIds: (prev.userIds || []).filter((id) => !remove.has(String(id))),
      }));
    }
    csvSelectionUserIdsRef.current = [];
    setRecipientBatchId('');
    setSelectedImportSummary(null);
    setCsvImportResult(null);
    setCsvFile(null);
    setCsvName('');
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = '';
    }
    toast.success('CSV seçimi kaldırıldı');
  };

  const handleSelectImport = async (batchId) => {
    try {
      const response = await api.get(`/api/admin/dashboard/notifications/recipients/imports/${batchId}`, {
        withCredentials: true,
      });
      const payload = response?.data?.data || response?.data || {};
      const validUserIds = Array.isArray(payload.validUserIds) ? payload.validUserIds : [];
      csvSelectionUserIdsRef.current = validUserIds.map(String);
      setRecipientBatchId(batchId);
      setRecipientInputMode('csv');
      setSelectedImportSummary({
        batchId,
        validCount:
          payload?.summary?.validCount ??
          payload?.validCount ??
          validUserIds.length ??
          0,
        totalRows: payload?.summary?.totalRows ?? payload?.totalRows ?? 0,
      });
      if (validUserIds.length > 0) {
        setFormData((prev) => ({
          ...prev,
          userIds: [...new Set([...(Array.isArray(prev.userIds) ? prev.userIds : []), ...validUserIds])],
        }));
      }
      toast.success('CSV import seçildi');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Import detayı alınamadı');
    }
  };

  const handleUpdateImportName = async (batchId) => {
    if (!requireManagePermission()) return;
    try {
      if (!editingImportName.trim()) {
        toast.error('Import adı boş olamaz');
        return;
      }
      await api.put(
        `/api/admin/dashboard/notifications/recipients/imports/${batchId}`,
        { name: editingImportName.trim() },
        { withCredentials: true }
      );
      setEditingImportId('');
      setEditingImportName('');
      await fetchRecipientImports(importsQuery);
      toast.success('Import adı güncellendi');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Import adı güncellenemedi');
    }
  };

  const handleArchiveImport = async (batchId) => {
    try {
      await api.delete(`/api/admin/dashboard/notifications/recipients/imports/${batchId}`, {
        withCredentials: true,
      });
      if (recipientBatchId === batchId) {
        const remove = new Set(csvSelectionUserIdsRef.current);
        if (remove.size > 0) {
          setFormData((prev) => ({
            ...prev,
            userIds: (prev.userIds || []).filter((id) => !remove.has(String(id))),
          }));
        }
        csvSelectionUserIdsRef.current = [];
        setRecipientBatchId('');
        setSelectedImportSummary(null);
      }
      await fetchRecipientImports(importsQuery);
      toast.success('Import arşivlendi');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Import arşivlenemedi');
    }
  };

  const handleUnarchiveImport = async (batchId) => {
    try {
      await api.post(`/api/admin/dashboard/notifications/recipients/imports/${batchId}/unarchive`, {}, {
        withCredentials: true,
      });
      await fetchRecipientImports(importsQuery);
      toast.success('Import arşivden çıkarıldı');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Import arşivden çıkarılamadı');
    }
  };

  const isImportArchived = (item) => {
    if (!item || typeof item !== 'object') return false;
    const explicitArchived = Boolean(
      item.isArchived ||
      item.archived ||
      item.archivedAt ||
      item.deletedAt ||
      item.status === 'archived' ||
      item.status === 'ARCHIVED' ||
      item.isActive === false
    );
    if (explicitArchived) return true;

    // Bazı backend response'larında arşiv durumu alanı gelmeyebiliyor.
    // "Arşivleri göster" açıkken ve status alanı hiç yoksa arşivli kabul et.
    const hasArchiveSignal =
      Object.prototype.hasOwnProperty.call(item, 'isArchived') ||
      Object.prototype.hasOwnProperty.call(item, 'archived') ||
      Object.prototype.hasOwnProperty.call(item, 'archivedAt') ||
      Object.prototype.hasOwnProperty.call(item, 'deletedAt') ||
      Object.prototype.hasOwnProperty.call(item, 'status') ||
      Object.prototype.hasOwnProperty.call(item, 'isActive');

    if (includeArchived && !hasArchiveSignal) return true;
    return false;
  };

  useEffect(() => {
    if (!recipientBatchId || !Array.isArray(imports) || imports.length === 0) return;
    const selected = imports.find((item) => item.batchId === recipientBatchId);
    if (selected) {
      setSelectedImportSummary((prev) => ({
        batchId: recipientBatchId,
        validCount: selected.validCount ?? prev?.validCount ?? 0,
        totalRows: selected.totalRows ?? prev?.totalRows ?? 0,
      }));
    }
  }, [recipientBatchId, imports]);

  // Modal açma/kapatma
  const handleCreateClick = () => {
    if (!requireManagePermission()) return;
    resetForm();
    fetchRecipientImports();
    // Aktif sekmeye göre sendType'ı ayarla
    setFormData((prev) => ({
      ...prev,
      sendType: activeTab,
    }));
    setShowCreateModal(true);
  };

  const handleEditClick = (notification) => {
    if (!requireManagePermission()) return;
    setEditingNotification(notification);
    // sendType'ı belirle
    let sendType = 'immediate';
    if (notification.eventType) {
      sendType = 'event_based';
    } else if (notification.recurringType) {
      // recurringType varsa (daily, weekly, monthly) -> recurring
      sendType = 'recurring';
    } else if (notification.scheduledTime && !notification.recurringType) {
      // Tek seferlik zamanlanmış bildirim -> immediate (scheduledTime ile)
      sendType = 'immediate';
    }

    // Channels: API formatından form formatına çevir
    let channels = ['push', 'websocket'];
    if (notification.channels && Array.isArray(notification.channels)) {
      const raw = notification.channels.flatMap(c =>
        typeof c === 'string' && c.includes(',')
          ? c.split(',').map(x => x.trim()).filter(Boolean)
          : [c].filter(Boolean)
      );
      channels = apiChannelsToForm(raw);
    } else if (typeof notification.channels === 'string') {
      const raw = notification.channels.split(',').map(c => c.trim()).filter(Boolean);
      channels = apiChannelsToForm(raw);
    }
    if (channels.length === 0) channels = ['push', 'websocket'];

    setFormData({
      type: notification.type || 'push',
      title: notification.title || '',
      body: notification.body || '',
      userIds: Array.isArray(notification.userIds) ? [...(notification.userIds || [])] : [],
      sendTo: notification.sendTo || 'all',
      priority: notification.priority || 'normal',
      channels: channels.length > 0 ? channels : ['push'],
      imageUrl: notification.imageUrl || '',
      data: normalizeNotificationData(notification.data),
      promotionData: notification.promotionData || null,
      sendType: sendType,
      scheduledTime: notification.scheduledTime || null,
      recurringType: notification.recurringType || null,
      recurringTime: notification.recurringTime || null,
      recurringDayOfWeek: notification.recurringDayOfWeek !== undefined ? notification.recurringDayOfWeek : null,
      recurringDayOfMonth: notification.recurringDayOfMonth !== undefined ? notification.recurringDayOfMonth : null,
      eventType: notification.eventType || null,
      officeCodes: notification.officeCodes || [],
      tahsilatGroups: notification.tahsilatGroups || [],
    });
    setRecipientBatchId(notification.recipientBatchId || '');
    setSelectedImportSummary(
      notification.recipientBatchId
        ? {
            batchId: notification.recipientBatchId,
            validCount: notification.sentCount || notification.sendCount || 0,
            totalRows: 0,
          }
        : null
    );
    fetchRecipientImports();
    setImagePreview(resolvePreviewUrl(notification.imageUrl || ''));
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedUserInputValue('');
    resetForm();
  };

  const handleImageUpload = (event) => {
    const inputElement = event.target;
    const file = inputElement.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin');
      inputElement.value = '';
      return;
    }

    // Dosya boyutunu kontrol et (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Görsel boyutu 5MB\'dan küçük olmalıdır');
      inputElement.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result?.toString() || '';
      if (!base64String) {
        toast.error('Görsel verisi okunamadı');
        inputElement.value = '';
        return;
      }

      // Base64 preview'i hemen göster
      setImagePreview(base64String);
      setImageUploading(true);

      try {
        const response = await api.post('/api/admin/dashboard/notifications/upload-image', {
          imageBase64: base64String,
          fileName: file.name,
        });

        const uploadedUrl = response.data?.data?.url || response.data?.url;

        if (!uploadedUrl) {
          throw new Error('Beklenmeyen yanıt: Görsel URL alınamadı');
        }

        const previewUrl = resolvePreviewUrl(uploadedUrl);

        // API başarılı olduğunda URL ile güncelle
        setImagePreview(previewUrl);
        setFormData((prev) => ({
          ...prev,
          imageUrl: uploadedUrl,
        }));
        toast.success('Görsel başarıyla yüklendi');
      } catch (err) {
        const message = err.response?.data?.message || err.message || 'Görsel yüklenemedi';
        toast.error(message);
        setImagePreview('');
      } finally {
        setImageUploading(false);
        if (inputElement) {
          inputElement.value = '';
        }
      }
    };
    reader.onerror = (err) => {
      toast.error('Görsel okunurken bir hata oluştu');
      setImagePreview('');
      setImageUploading(false);
      if (inputElement) {
        inputElement.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview('');
    setFormData((prev) => ({
      ...prev,
      imageUrl: '',
    }));
    setImageUploading(false);
  };

  // Bildirim oluştur
  const handleCreate = async () => {
    if (!requireManagePermission()) return;
    try {
      // Validation: En az bir kanal seçilmeli
      if (!formData.channels || formData.channels.length === 0) {
        toast.error('En az bir gönderim kanalı seçilmelidir');
        return;
      }

      // Validation: sendType'a göre gerekli alanları kontrol et
      if (formData.sendType === 'scheduled' && !formData.scheduledTime) {
        toast.error('Zamanlanmış bildirim için lütfen tarih ve saat seçin');
        return;
      }
      if (formData.sendType === 'recurring') {
        // Tekrarlayan bildirim için recurringType ve recurringTime zorunlu
        if (!formData.recurringType) {
          toast.error('Tekrarlayan bildirim için lütfen tekrarlama sıklığı seçin');
          return;
        }
        if (!formData.recurringTime) {
          toast.error('Tekrarlayan bildirim için lütfen gönderim saati seçin');
          return;
        }
      }
      if (formData.sendType === 'event_based' && !formData.eventType) {
        toast.error('Etkinlik bazlı bildirim için lütfen etkinlik tipi seçin');
        return;
      }
      if (formData.sendTo === 'tahsilat' && (!formData.tahsilatGroups || formData.tahsilatGroups.length === 0)) {
        toast.error('Tahsilat hedefi için en az bir tahsilat kategorisi seçin');
        return;
      }
      if (formData.sendTo === 'selected' && (!formData.userIds || formData.userIds.length === 0) && !recipientBatchId) {
        toast.error('Seçili kullanıcılar için en az bir kullanıcı ID / TC ekleyin veya bir CSV import seçin');
        return;
      }

      // sendType'a göre notificationData hazırla
      const isImmediateAndNotScheduled = formData.sendType === 'immediate' && !formData.scheduledTime;
      const notificationData = {
        ...formData,
        // Channels array'ini gönder (mutlaka gönderilmeli)
        channels: formChannelsToApi(formData.channels.length > 0 ? formData.channels : ['push', 'websocket']),
        // sendType'a göre ilgili alanları ayarla
        scheduledTime: formData.sendType === 'scheduled'
          ? (formData.scheduledTime || undefined)
          : formData.sendType === 'immediate' && formData.scheduledTime
            ? (formData.scheduledTime || undefined)
            : undefined,
        recurringType: formData.sendType === 'recurring' && formData.recurringType
          ? (formData.recurringType || undefined)
          : undefined,
        recurringTime: formData.sendType === 'recurring' && formData.recurringType
          ? (formData.recurringTime || undefined)
          : formData.sendType === 'event_based' && formData.recurringTime
            ? (formData.recurringTime || undefined)
            : undefined,
        eventType: formData.sendType === 'event_based'
          ? (formData.eventType || undefined)
          : undefined,
        // OfficeCodes: Sadece sendTo === 'all' iken ve dolu ise gönder
        officeCodes: formData.sendTo === 'all' && formData.officeCodes && formData.officeCodes.length > 0
          ? formData.officeCodes
          : undefined,
        tahsilatGroups: formData.sendTo === 'tahsilat' && formData.tahsilatGroups && formData.tahsilatGroups.length > 0
          ? formData.tahsilatGroups
          : undefined,
        // Immediate bildirimler için _immediateSend flag'i ekle (reducer'da kullanmak için)
        _immediateSend: isImmediateAndNotScheduled,
        // NOT: Status backend tarafından otomatik belirlenir:
        // - scheduledTime varsa -> status: "scheduled"
        // - scheduledTime yoksa -> status: "draft" veya "sent" (anında gönderilirse)
        // Bu yüzden frontend'den status göndermiyoruz
      };

      if (formData.sendTo === 'selected') {
        notificationData.userIds = Array.isArray(formData.userIds) ? formData.userIds : [];
        if (recipientBatchId) {
          notificationData.recipientBatchId = recipientBatchId;
        }
      }
      delete notificationData.sendType;

      // Phase 1.1: Optimistic Update - Temp ID ile bildirimi hemen ekle
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const optimisticNotification = {
        ...notificationData,
        id: tempId,
        status: isImmediateAndNotScheduled ? 'sending' : (formData.scheduledTime ? 'scheduled' : 'draft'),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistic update'i Redux'a ekle
      dispatch(addOptimisticNotification({ tempId, notification: optimisticNotification }));

      // Modal'ı hemen kapat (await'ten önce)
      handleCloseModal();

      // Bildirim oluşturma işlemini başlat (await etmeden)
      const createPromise = dispatch(createNotification(notificationData));

      // Arka planda işlemleri yap
      (async () => {
        try {
          const createdNotification = await createPromise.unwrap();

          // Phase 1.1: Optimistic Update Commit - Temp ID'yi gerçek ID ile değiştir
          dispatch(commitOptimisticUpdate({ tempId, realId: createdNotification.id }));

          // Eğer hemen gönderilecekse (scheduledTime yoksa), arka planda gönder
          if (isImmediateAndNotScheduled) {
            // Status zaten reducer tarafından "sending" yapıldı (_immediateSend flag'i sayesinde)
            // sendNotification'ı arka planda başlat
            try {
              // Kategoriyi belirle
              const category = getCategoryFromTab('immediate');
              await dispatch(sendNotification({ id: createdNotification.id, category })).unwrap();

              toast.success('Bildirim başarıyla oluşturuldu ve gönderildi');

              // Status'u "sent" yap (sendNotification zaten status'u güncelliyor olabilir, ama emin olmak için)
              // WebSocket event'i de status'u güncelleyecek, bu yüzden fetchNotifications çağırmaya gerek yok
              // Sadece stats'ı güncelle
              dispatch(fetchNotificationStats({ force: true }));
            } catch (sendError) {
              toast.error(sendError || 'Bildirim oluşturuldu ancak gönderilemedi', { duration: 5000 });

              // Status'u "failed" yap
              try {
                await dispatch(updateNotificationStatus({
                  id: createdNotification.id,
                  statusData: { status: 'failed' }
                })).unwrap();
              } catch (statusError) {
                // Hata sessizce yok sayılıyor
              }

              // Stats'ı güncelle
              dispatch(fetchNotificationStats({ force: true }));
            }
          } else {
            if (formData.sendType === 'immediate' && formData.scheduledTime) {
              toast.success('Tek seferlik zamanlanmış bildirim başarıyla oluşturuldu');
            } else {
              toast.success('Bildirim başarıyla oluşturuldu');
            }

            // Diğer bildirim türleri için sadece stats'ı güncelle
            // Bildirim zaten reducer tarafından state'e eklendi
            dispatch(fetchNotificationStats({ force: true }));
            
            // Eğer scheduled veya recurring ise, liste güncellemesi gerekebilir
            if (formData.sendType !== 'immediate' || formData.scheduledTime) {
              const category = getCategoryFromTab(activeTab);
              dispatch(fetchNotifications({ 
                category, 
                page: pagination.page, 
                limit: pagination.limit, 
                merge: false,
                force: true
              }));
            }
          }
        } catch (error) {
          // Phase 1.1: Rollback Optimistic Update
          dispatch(rollbackOptimisticUpdate({ tempId }));
          
          // Phase 1.2: Error Handling & User Feedback
          const errorType = classifyError(error);
          const errorMessage = ERROR_TYPES[errorType] || error?.message || 'Bildirim oluşturulamadı';
          
          toast.error(errorMessage, {
            duration: 5000,
            action: errorType === 'NETWORK_ERROR' ? {
              label: 'Tekrar Dene',
              onClick: () => {
                // Retry logic buraya eklenebilir
              }
            } : null
          });
        }
      })();
      
      // Not: WebSocket event'i (notification-created) de dinleniyor
      // Bu sayede başka kullanıcıların bildirimleri de anında görünecek
    } catch (error) {
      // Phase 1.2: Error Handling
      const errorType = classifyError(error);
      const errorMessage = ERROR_TYPES[errorType] || error?.message || 'Bildirim oluşturulamadı';
      toast.error(errorMessage);
    }
  };

  // Bildirim güncelle
  const handleUpdate = async () => {
    if (!requireManagePermission()) return;
    try {
      // Validation: En az bir kanal seçilmeli
      if (!formData.channels || formData.channels.length === 0) {
        toast.error('En az bir gönderim kanalı seçilmelidir');
        return;
      }

      // Validation: sendType'a göre gerekli alanları kontrol et
      if (formData.sendType === 'scheduled' && !formData.scheduledTime) {
        toast.error('Zamanlanmış bildirim için lütfen tarih ve saat seçin');
        return;
      }
      if (formData.sendType === 'recurring') {
        // Tekrarlayan bildirim için recurringType ve recurringTime zorunlu
        if (!formData.recurringType) {
          toast.error('Tekrarlayan bildirim için lütfen tekrarlama sıklığı seçin');
          return;
        }
        if (!formData.recurringTime) {
          toast.error('Tekrarlayan bildirim için lütfen gönderim saati seçin');
          return;
        }
      }
      if (formData.sendType === 'event_based' && !formData.eventType) {
        toast.error('Etkinlik bazlı bildirim için lütfen etkinlik tipi seçin');
        return;
      }
      if (formData.sendTo === 'tahsilat' && (!formData.tahsilatGroups || formData.tahsilatGroups.length === 0)) {
        toast.error('Tahsilat hedefi için en az bir tahsilat kategorisi seçin');
        return;
      }
      if (formData.sendTo === 'selected' && (!formData.userIds || formData.userIds.length === 0) && !recipientBatchId) {
        toast.error('Seçili kullanıcılar için en az bir kullanıcı ID / TC ekleyin veya bir CSV import seçin');
        return;
      }

      // sendType'a göre notificationData hazırla
      const notificationData = {
        ...formData,
        // Channels array'ini gönder (mutlaka gönderilmeli)
        channels: formChannelsToApi(formData.channels.length > 0 ? formData.channels : ['push', 'websocket']),
        // sendType'a göre ilgili alanları ayarla
        scheduledTime: formData.sendType === 'scheduled'
          ? (formData.scheduledTime || undefined)
          : formData.sendType === 'immediate' && formData.scheduledTime
            ? (formData.scheduledTime || undefined)
            : undefined,
        recurringType: formData.sendType === 'recurring' && formData.recurringType
          ? (formData.recurringType || undefined)
          : undefined,
        recurringTime: formData.sendType === 'recurring' && formData.recurringType
          ? (formData.recurringTime || undefined)
          : formData.sendType === 'event_based' && formData.recurringTime
            ? (formData.recurringTime || undefined)
            : undefined,
        // recurringDayOfWeek: Sadece weekly için gönder
        recurringDayOfWeek: formData.sendType === 'recurring' && formData.recurringType === 'weekly' && formData.recurringDayOfWeek !== null && formData.recurringDayOfWeek !== undefined
          ? formData.recurringDayOfWeek
          : undefined,
        // recurringDayOfMonth: Sadece monthly için gönder
        recurringDayOfMonth: formData.sendType === 'recurring' && formData.recurringType === 'monthly' && formData.recurringDayOfMonth !== null && formData.recurringDayOfMonth !== undefined
          ? formData.recurringDayOfMonth
          : undefined,
        eventType: formData.sendType === 'event_based'
          ? (formData.eventType || undefined)
          : undefined,
        // OfficeCodes: Sadece sendTo === 'all' iken ve dolu ise gönder
        officeCodes: formData.sendTo === 'all' && formData.officeCodes && formData.officeCodes.length > 0
          ? formData.officeCodes
          : undefined,
        tahsilatGroups: formData.sendTo === 'tahsilat' && formData.tahsilatGroups && formData.tahsilatGroups.length > 0
          ? formData.tahsilatGroups
          : undefined,
        // sendType'a göre status'u ayarla
        status: formData.sendType === 'immediate'
          ? (formData.scheduledTime ? 'scheduled' : 'draft')
          : formData.sendType === 'scheduled' && formData.scheduledTime
            ? 'scheduled'
            : formData.sendType === 'recurring' && formData.recurringType
              ? 'scheduled'
              : formData.sendType === 'event_based'
                ? 'scheduled'
                : (editingNotification?.status === 'scheduled' ? 'draft' : 'draft'),
      };

      if (formData.sendTo === 'selected') {
        notificationData.userIds = Array.isArray(formData.userIds) ? formData.userIds : [];
        if (recipientBatchId) {
          notificationData.recipientBatchId = recipientBatchId;
        }
      }
      delete notificationData.sendType;

      // Kategoriyi belirle
      const updateCategory = getCategoryFromTab(formData.sendType === 'immediate' ? 'immediate' :
        (formData.sendType === 'event_based' ? 'event_based' : activeTab));

      await dispatch(
        updateNotification({
          id: editingNotification.id,
          notificationData,
          category: updateCategory,
        })
      ).unwrap();

      toast.success('Bildirim başarıyla güncellendi');
      handleCloseModal();
      const category = getCategoryFromTab(formData.sendType === 'immediate' ? 'immediate' : (formData.sendType === 'event_based' ? 'event_based' : activeTab));
      dispatch(fetchNotifications({ category, page: pagination.page, limit: pagination.limit, merge: false, force: true }));
      dispatch(fetchNotificationStats());
    } catch (error) {
      // Backend'den gelen hata mesajını kullan
      const errorMessage = error?.response?.data?.message 
        || error?.response?.data?.error 
        || error?.message 
        || 'Bildirim güncellenemedi';
      
      toast.error(errorMessage);
    }
  };

  // Bildirim silme modal'ını aç
  const handleDeleteClick = (notification) => {
    if (!requireManagePermission()) return;
    if (!notification) {
      toast.error('Bildirim bulunamadı');
      return;
    }

    const deleteInfo = getNotificationDeleteInfo(notification);
    if (!deleteInfo.canOpenModal) {
      toast.error(deleteInfo.message, { duration: 5000, id: 'notification-delete-blocked' });
      return;
    }

    setDeleteConfirm({ show: true, notification, deleteInfo });
  };

  // Bildirim sil
  const handleDelete = async (notification) => {
    if (!notification) return;

    const deleteInfo = getNotificationDeleteInfo(notification);
    if (!deleteInfo.canDelete) {
      toast.error(deleteInfo.message, { duration: 5000 });
      setDeleteConfirm({ show: false, notification: null, deleteInfo: null });
      return;
    }

    try {
      setIsDeleting(true);

      if (deleteInfo.requiresCancelFirst) {
        const cancelResult = await dispatch(
          updateNotificationStatus({
            id: notification.id,
            statusData: { status: 'cancelled' },
          })
        );

        if (updateNotificationStatus.rejected.match(cancelResult)) {
          toast.error(
            parseNotificationDeleteError(cancelResult.payload, 'Zamanlama iptal edilemedi'),
            { duration: 5000 }
          );
          return;
        }
      }

      const category = notification.eventType
        ? 'event-based'
        : (notification.recurringType || notification.scheduledTime ? 'recurring' : 'instant');

      const result = await dispatch(deleteNotification({ id: notification.id, category }));

      if (deleteNotification.fulfilled.match(result)) {
        toast.success('Bildirim başarıyla silindi');
        dispatch(fetchNotificationStats());
      } else if (deleteNotification.rejected.match(result)) {
        toast.error(parseNotificationDeleteError(result.payload), { duration: 5000 });
      } else {
        toast.error('Bildirim silinemedi', { duration: 5000 });
      }
    } catch (error) {
      toast.error(parseNotificationDeleteError(error), { duration: 5000 });
    } finally {
      setIsDeleting(false);
      setDeleteConfirm({ show: false, notification: null, deleteInfo: null });
    }
  };

  // Bildirimi hemen gönder
  const handleSend = async (id) => {
    if (!requireManagePermission()) return;
    if (!window.confirm('Bu bildirimi hemen göndermek istediğinize emin misiniz?')) {
      return;
    }

    try {
      // Kategoriyi belirle (bildirimi bulup kategorisini belirle)
      const notification = notifications.find(n => n.id === id);

      let category;
      if (notification) {
        // Event-based kontrolü (en öncelikli)
        if (notification.eventType) {
          category = 'event-based';
        }
        // Recurring kontrolü (sadece geçerli recurringType varsa)
        else if (notification.recurringType &&
          (notification.recurringType === 'daily' ||
            notification.recurringType === 'weekly' ||
            notification.recurringType === 'monthly')) {
          category = 'recurring';
        }
        // Tek seferlik zamanlanmış (scheduledTime var ama recurringType yok)
        else if (notification.scheduledTime) {
          category = 'instant';
        }
        // Anlık bildirim
        else {
          category = 'instant';
        }
      } else {
        category = getCategoryFromTab(activeTab);
      }

      await dispatch(sendNotification({ id, category })).unwrap();

      toast.success('Bildirim başarıyla gönderildi!');

      // Gönderim sonrası listeyi ve stats'ı güncelle
      // Backend'in DB'yi güncellemesi için bekleme
      await new Promise(resolve => setTimeout(resolve, 1500));

      dispatch(fetchNotificationStats());
    } catch (error) {
      toast.error(error || 'Bildirim gönderilemedi');
    }
  };


  // Bildirim status'unu değiştir
  const handleStatusChange = async (id, newStatus) => {
    try {
      const notification = notifications.find(n => n.id === id);
      if (!notification) {
        toast.error('Bildirim bulunamadı');
        return;
      }

      // Backend kuralı: scheduled + recurring/event yoksa scheduledTime zorunlu
      // Bu durumda kullanıcıyı düzenleme modalına yönlendirip alanı vurgula
      if (
        newStatus === 'scheduled' &&
        !notification.recurringType &&
        !notification.eventType &&
        !notification.scheduledTime
      ) {
        setHighlightScheduledTime(true);
        handleEditClick(notification);
        setFormData((prev) => ({
          ...prev,
          sendType: 'scheduled',
          status: 'scheduled',
        }));
        toast.error('Zamanlanmış durum için tarih-saat zorunlu. Lütfen zamanlama alanını doldurun.');
        return;
      }

      const statusData = {
        status: newStatus,
      };

      await dispatch(
        updateNotificationStatus({
          id,
          statusData,
        })
      ).unwrap();

      toast.success(`Bildirim status'u "${getStatusLabel(newStatus)}" olarak güncellendi`);

      // Liste ve istatistikleri güncelle
      // const category = getCategoryFromTab(activeTab);
      // dispatch(fetchNotifications({
      //   category,
      //   page: pagination.page,
      //   limit: pagination.limit,
      //   merge: false
      // }));
      dispatch(fetchNotificationStats());
    } catch (error) {
      toast.error(error || 'Status güncellenemedi');
    }
  };

  // Bildirimi zamanla
  const handleSchedule = async (id) => {
    if (!requireManagePermission()) return;
    const scheduledTime = prompt('Zamanlama tarihi (YYYY-MM-DD HH:mm:ss formatında, örn: 2025-12-03 15:00:00):');
    if (!scheduledTime) return;

    try {
      await dispatch(
        updateNotificationStatus({
          id,
          statusData: {
            status: 'scheduled',
            scheduledTime,
          },
        })
      ).unwrap();
      toast.success('Bildirim zamanlandı');
      // const category = getCategoryFromTab(activeTab);
      // dispatch(fetchNotifications({ category, page: pagination.page, limit: pagination.limit, merge: false }));
      dispatch(fetchNotificationStats());
    } catch (error) {
      toast.error(error || 'Bildirim zamanlanamadı');
    }
  };

  // İstatistikler
  const totalNotifications = stats?.total || notifications.length;
  const activeNotifications =
    stats?.byStatus?.sent || notifications.filter((n) => n.status === 'sent').length;
  const scheduledNotifications =
    stats?.byStatus?.scheduled || notifications.filter((n) => n.status === 'scheduled').length;

  // Toplam gönderim sayısı: Backend'den stats'tan geliyorsa onu kullan, yoksa notifications array'inden hesapla
  // Backend'den totals.totalSent geliyor
  const totalSent = stats?.totals?.totalSent !== undefined
    ? stats.totals.totalSent
    : stats?.totalSent !== undefined
      ? stats.totalSent
      : stats?.totalSentCount !== undefined
        ? stats.totalSentCount
        : stats?.sentCount !== undefined
          ? stats.sentCount
          : notifications.reduce((sum, n) => sum + (n.sentCount || 0), 0);
  const paginationTotalPages = Math.max(
    pagination?.totalPages || 0,
    Math.ceil((pagination?.total || 0) / Math.max(1, pagination?.limit || 1)),
    1
  );
  return (
    <div className="space-y-4">
      <ModalReporter open={showCreateModal || showEditModal || deleteConfirm.show} />
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Bildirim Yönetimi</h1>
            <p className="text-gray-500 mt-1">Kullanıcı bildirimlerini oluşturun ve yönetin</p>
          </div>
        </div>

        {/* Tabs */}
        <LimelightNav
          items={[
            { id: 'immediate', icon: <Bell />, label: 'Bildirim' },
            { id: 'recurring', icon: <RefreshCw />, label: 'Tekrarlayan' },
            { id: 'event_based', icon: <Target />, label: 'Etkinlik Bazlı' },
          ]}
          showLabels
          activeIndex={['immediate', 'recurring', 'event_based'].indexOf(activeTab)}
          onTabChange={(i) => setActiveTab(['immediate', 'recurring', 'event_based'][i])}
          className="bg-gray-50 border border-gray-100"
          limelightClassName="bg-blue-500"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-50 rounded-2xl mb-4">
            <Bell className="w-6 h-6 text-blue-600" />
          </div>
          <p className="text-sm text-gray-500 font-medium mb-1">Toplam Bildirim</p>
          <p className="text-lg font-bold text-gray-900 tracking-tight">
            {statsLoading ? '...' : totalNotifications}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-center w-12 h-12 bg-green-50 rounded-2xl mb-4">
            <Send className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-sm text-gray-500 font-medium mb-1">Gönderildi</p>
          <p className="text-lg font-bold text-gray-900 tracking-tight">
            {statsLoading ? '...' : activeNotifications}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-center w-12 h-12 bg-amber-50 rounded-2xl mb-4">
            <Calendar className="w-6 h-6 text-amber-600" />
          </div>
          <p className="text-sm text-gray-500 font-medium mb-1">Zamanlanmış</p>
          <p className="text-lg font-bold text-gray-900 tracking-tight">
            {statsLoading ? '...' : scheduledNotifications}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-center w-12 h-12 bg-purple-50 rounded-2xl mb-4">
            <Users className="w-6 h-6 text-purple-600" />
          </div>
          <p className="text-sm text-gray-500 font-medium mb-1">Toplam Gönderim</p>
          <p className="text-lg font-bold text-gray-900 tracking-tight">
            {statsLoading ? '...' : totalSent.toLocaleString('tr-TR')}
          </p>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Filters Group */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 flex-wrap flex-1">
            {/* Search */}
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Bildirim ara veya TC No gir..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Tarih Filtreleri */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="pl-3 pr-2 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-600"
                  placeholder="Başlangıç"
                />
              </div>
              <span className="text-gray-400">-</span>
              <div className="relative">
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="pl-3 pr-2 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-600"
                  placeholder="Bitiş"
                />
              </div>
            </div>

            {/* Tip Filtresi */}
            <Dropdown
              items={[
                { value: '', label: 'Tüm Tipler' },
                { value: 'push', label: 'Push' },
                { value: 'promotion', label: 'Promosyon' },
                { value: 'system', label: 'Sistem' },
                { value: 'websocket', label: 'WebSocket' },
              ]}
              value={filterType}
              onChange={setFilterType}
              placeholder="Tüm Tipler"
              simple
              className="min-w-[200px]"
              buttonClassName="py-2 text-sm"
            />

            {/* Durum Filtresi */}
            <Dropdown
              items={[
                { value: '', label: 'Tüm Durumlar' },
                { value: 'draft', label: 'Taslak' },
                { value: 'scheduled', label: 'Zamanlanmış' },
                { value: 'sending', label: 'Gönderiliyor' },
                { value: 'sent', label: 'Gönderildi' },
                { value: 'failed', label: 'Başarısız' },
                { value: 'cancelled', label: 'İptal Edildi' },
              ]}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Tüm Durumlar"
              simple
              className="min-w-[200px]"
              buttonClassName="py-2 text-sm"
            />

            {/* Temizle Butonu */}
            {(filterType || filterStatus || filterStartDate || filterEndDate || searchTerm) && (
              <button
                onClick={() => {
                  setFilterType('');
                  setFilterStatus('');
                  setFilterStartDate('');
                  setFilterEndDate('');
                  setSearchTerm('');
                }}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Filtreleri Temizle"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Create Button */}
          <div className="flex-shrink-0">
            <button
              onClick={handleCreateClick}
              className={`flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-xl transition-colors font-medium whitespace-nowrap ${permissionGuardClass(canManageNotifications)} ${canManageNotifications ? 'hover:bg-blue-600' : ''}`}
              aria-disabled={!canManageNotifications}
            >
              <Plus className="w-5 h-5" /> Yeni Bildirim
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {(() => {
          // Phase 1.1: Duplicate bildirimleri temizle (optimistic update sonrası çakışmaları önlemek için)
          const uniqueNotifications = notifications.reduce((acc, notification) => {
            const existingIndex = acc.findIndex(n => n.id === notification.id);
            if (existingIndex === -1) {
              acc.push(notification);
            } else {
              // Duplicate varsa, daha yeni olanı kullan (updatedAt'e göre)
              const existing = acc[existingIndex];
              const existingTime = existing.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
              const newTime = notification.updatedAt ? new Date(notification.updatedAt).getTime() : 0;
              if (newTime > existingTime) {
                acc[existingIndex] = notification;
              }
            }
            return acc;
          }, []);
          
          const filteredNotifications = uniqueNotifications.filter((notification) => {
            // 1. Sekme Filtrelemesi
            const isTabMatch = (() => {
              if (activeTab === 'immediate') {
                const isAnlık = !notification.scheduledTime &&
                  !notification.recurringType &&
                  !notification.eventType &&
                  notification.status !== 'scheduled';

                const hasScheduledTime = notification.scheduledTime &&
                  notification.scheduledTime !== null &&
                  notification.scheduledTime !== '';

                const isOneTimeScheduled = hasScheduledTime &&
                  !notification.recurringType &&
                  !notification.eventType;

                return isAnlık || isOneTimeScheduled;
              } else if (activeTab === 'recurring') {
                return notification.recurringType &&
                  (notification.recurringType === 'daily' ||
                    notification.recurringType === 'weekly' ||
                    notification.recurringType === 'monthly');
              } else if (activeTab === 'event_based') {
                return !!notification.eventType;
              }
              return true;
            })();

            if (!isTabMatch) return false;

            // 2. Arama/Search Filtrelemesi (Client-side Fallback)
            if (searchTerm) {
              const isTC = /^\d{11}$/.test(searchTerm);

              // TC araması ise client-side filtreleme yapma (backend'e güven), 
              // çünkü TC numarası bildirim metninde geçmeyebilir ama backend User ID eşleşmesi bulmuştur.
              if (isTC) {
                return true;
              }

              // Normal Metin Araması: Backend "z" araması için sonuç döndürse bile,
              // eğer title veya body içinde geçmiyorsa (backend hatası veya geniş eşleşme),
              // kullanıcıya gösterme.
              const term = searchTerm.toLowerCase();
              const matchesTitle = notification.title && notification.title.toLowerCase().includes(term);
              const matchesBody = notification.body && notification.body.toLowerCase().includes(term);
              return matchesTitle || matchesBody;
            }

            return true;
          });


          if (loading && filteredNotifications.length === 0) {
            return <div className="p-12 text-center text-gray-500">Yükleniyor...</div>;
          }

          if (filteredNotifications.length === 0) {
            return (
              <div className="text-center py-12">
                <Bell className="mx-auto h-12 w-12 text-gray-300" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Bildirim bulunamadı</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {activeTab === 'immediate' && 'Henüz anlık bildirim oluşturulmamış.'}
                  {activeTab === 'recurring' && 'Henüz tekrarlayan veya zamanlanmış bildirim oluşturulmamış.'}
                  {activeTab === 'event_based' && 'Henüz etkinlik bazlı bildirim oluşturulmamış.'}
                </p>
              </div>
            );
          }

          return (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      BAŞLIK
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      İÇERİK
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      GÖNDERİM KANALLARI
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      HEDEF
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      GÖNDERİM
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      DURUM
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      İŞLEMLER
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredNotifications.map((notification, index) => (
                    <tr key={`${notification.id}-${index}-${notification.createdAt || Date.now()}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Bell className="text-blue-600 w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{notification.title || '-'}</p>
                            <p className="text-xs text-gray-500">
                              {notification.createdAt ? (notification.createdAt.includes('T') ? formatDate(notification.createdAt) : notification.createdAt) : '-'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-600 max-w-xs truncate">
                          {notification.body || '-'}
                        </p>
                        {/* Tekrarlayan bildirim bilgisi */}
                        {formatRecurringInfo(notification) && (
                          <p className="text-xs text-blue-600 mt-1 font-medium">
                            {formatRecurringInfo(notification)}
                          </p>
                        )}
                        {/* Tek seferlik zamanlanmış bildirim bilgisi - sadece saat */}
                        {formatScheduledTimeOnly(notification) && (
                          <p className="text-xs text-blue-600 mt-1 font-medium">
                            {formatScheduledTimeOnly(notification)}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {notification.channels && notification.channels.length > 0 ? (
                            notification.channels.map((channel) => {
                              const channelLabels = {
                                push: { label: '📱 Push', color: 'bg-blue-100 text-blue-800' },
                                websocket: { label: '🔔 WebSocket', color: 'bg-green-100 text-green-800' },
                                websocket_only: { label: '🔔 WebSocket Only', color: 'bg-green-100 text-green-800' },
                                firebase: { label: '🔥 Firebase', color: 'bg-orange-100 text-orange-800' },
                                firebase_only: { label: '🔥 Firebase Only', color: 'bg-orange-100 text-orange-800' },
                                push_only: { label: '📱 Push Only', color: 'bg-blue-100 text-blue-800' },
                                email: { label: '📧 E-posta', color: 'bg-yellow-100 text-yellow-800' },
                                sms: { label: '💬 SMS', color: 'bg-purple-100 text-purple-800' },
                              };
                              const channelInfo = channelLabels[channel] || { label: channel, color: 'bg-gray-100 text-gray-800' };
                              return (
                                <span
                                  key={channel}
                                  className={`px-2 py-1 rounded-full text-xs font-semibold ${channelInfo.color}`}
                                >
                                  {channelInfo.label}
                                </span>
                              );
                            })
                          ) : (
                            <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                              -
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {notification.sendTo === 'all'
                            ? 'Tümü'
                            : notification.sendTo === 'tahsilat'
                              ? (notification.tahsilatGroups?.length
                                  ? `Tahsilat: ${notification.tahsilatGroups.join(', ')}`
                                  : 'Tahsilat kategorileri')
                              : `${notification.userIds?.length || 0} Kullanıcı`}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {notification.sentCount?.toLocaleString('tr-TR') || 0}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Dropdown
                          items={[
                            { value: 'draft', label: 'Taslak' },
                            { value: 'scheduled', label: 'Zamanlanmış' },
                            { value: 'sent', label: 'Gönderildi' },
                            { value: 'failed', label: 'Başarısız' },
                            { value: 'cancelled', label: 'İptal Edildi' },
                          ]}
                          value={notification.status || 'draft'}
                          onChange={(newStatus) => {
                            if (newStatus !== notification.status) {
                              handleStatusChange(notification.id, newStatus);
                            }
                          }}
                          simple
                          className="min-w-[200px]"
                          buttonClassName={`px-3 py-1 text-xs font-semibold rounded-full border-0 ${getStatusColor(notification.status)}`}
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {notification.status === 'draft' && (
                            <button
                              onClick={() => handleSend(notification.id)}
                              className={`p-2 text-blue-600 rounded-lg transition-colors ${permissionGuardClass(canManageNotifications)} ${canManageNotifications ? 'hover:bg-blue-50' : ''}`}
                              title="Hemen Gönder"
                              aria-disabled={!canManageNotifications}
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          {notification.status === 'draft' && !notification.scheduledTime && (
                            <button
                              onClick={() => handleSchedule(notification.id)}
                              className={`p-2 text-orange-600 rounded-lg transition-colors ${permissionGuardClass(canManageNotifications)} ${canManageNotifications ? 'hover:bg-orange-50' : ''}`}
                              title="Zamanla"
                              aria-disabled={!canManageNotifications}
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleEditClick(notification)}
                            className={`p-2 text-gray-600 rounded-lg transition-colors ${permissionGuardClass(canManageNotifications)} ${canManageNotifications ? 'hover:bg-gray-100' : ''}`}
                            title="Düzenle"
                            aria-disabled={!canManageNotifications}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {(() => {
                            const deleteInfo = getNotificationDeleteInfo(notification);
                            const canDeleteAction = canManageNotifications && deleteInfo.canOpenModal;
                            return (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!canManageNotifications) {
                                    toast.error(PERMISSION_DENIED_MESSAGE);
                                    return;
                                  }
                                  handleDeleteClick(notification);
                                }}
                                className={`p-2 rounded-lg transition-colors ${
                                  canDeleteAction
                                    ? 'text-red-600 hover:bg-red-50'
                                    : 'text-red-300 opacity-50 cursor-not-allowed'
                                }`}
                                title={
                                  !canManageNotifications
                                    ? PERMISSION_DENIED_MESSAGE
                                    : deleteInfo.canOpenModal
                                      ? 'Sil'
                                      : deleteInfo.message
                                }
                                aria-disabled={!canDeleteAction}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Pagination — UserList ile aynı bileşen ve düzen */}
        <div className="flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-gray-50/30">
          <p className="text-sm text-gray-500 font-medium">
            Toplam{' '}
            <span className="font-bold text-gray-900">{pagination?.total ?? 0}</span> kayıt
            {paginationTotalPages > 1 && (
              <span className="ml-2 text-gray-400">
                • Sayfa {pagination.page} / {paginationTotalPages}
              </span>
            )}
          </p>
          <NumberedPagination
            currentPage={pagination.page}
            totalPages={paginationTotalPages}
            paginationItemsToDisplay={5}
            onPageChange={(page) => {
              const category = getCategoryFromTab(activeTab);
              const isTC = /^\d{11}$/.test(searchTerm);
              dispatch(
                fetchNotifications({
                  category,
                  type: filterType || undefined,
                  status: filterStatus || undefined,
                  startDate: filterStartDate ? new Date(filterStartDate).toISOString() : undefined,
                  endDate: filterEndDate ? new Date(filterEndDate).toISOString() : undefined,
                  customerId: isTC ? searchTerm : undefined,
                  search: !isTC ? searchTerm || undefined : undefined,
                  page,
                  limit: pagination.limit,
                  merge: false,
                })
              );
            }}
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && createPortal((
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-4xl w-full max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {showEditModal ? 'Bildirim Düzenle' : 'Yeni Bildirim Oluştur'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="text-gray-600 w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* 1. Bildirim Başlığı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bildirim Başlığı *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn: Yeni Kampanya!"
                  required
                />
              </div>

              {/* 2. Mesaj İçeriği */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mesaj İçeriği *
                </label>
                <textarea
                  rows="4"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bildirim mesajınızı buraya yazın..."
                  required
                />
              </div>

              {/* 3. Görsel Yükleme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bildirim Görseli (Opsiyonel)
                </label>
                <div className="flex flex-col gap-3 p-4 border border-dashed border-gray-300 rounded-xl bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label
                      className={`inline-flex items-center justify-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors ${imageUploading ? 'cursor-not-allowed opacity-60 pointer-events-none' : 'cursor-pointer hover:bg-gray-100'
                        }`}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={imageUploading}
                        onChange={handleImageUpload}
                      />
                      {imageUploading ? 'Yükleniyor...' : 'Görsel Yükle'}
                    </label>
                    {imagePreview ? (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        disabled={imageUploading}
                        className="self-start sm:self-center px-4 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Görseli Kaldır
                      </button>
                    ) : null}
                  </div>
                  {imageUploading ? (
                    <p className="text-xs text-blue-500">Görsel yükleniyor, lütfen bekleyin...</p>
                  ) : null}
                  <p className="text-xs text-gray-500">
                    Desteklenen formatlar: JPG, PNG, GIF. Dosya tarayıcı tarafından base64 formatına dönüştürülerek kaydedilir.
                  </p>
                  {imagePreview ? (
                    <div className="mt-2">
                      <span className="text-xs text-gray-500 block mb-2">Önizleme:</span>
                      <img
                        src={imagePreview}
                        alt="Bildirim görseli"
                        className="max-h-40 rounded-lg border border-gray-200 object-contain"
                      />
                    </div>
                  ) : null}
                  {!imagePreview && formData.imageUrl ? (
                    <p className="text-xs text-gray-500">
                      Mevcut görsel URL&apos;si kullanılacak: {formData.imageUrl}
                    </p>
                  ) : null}
                </div>
              </div>

              {/* 4. Gönderim Hedefi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gönderim Hedefi
                </label>
                <Dropdown
                  items={[
                    { value: 'all', label: 'Tüm kullanıcılar' },
                    { value: 'selected', label: 'Seçili kullanıcılar' },
                    ...(formData.sendType !== 'event_based' ? [{ value: 'tahsilat', label: 'Tahsilat kategorileri' }] : []),
                  ]}
                  value={formData.sendTo}
                  onChange={(newSendTo) => {
                    setFormData({
                      ...formData,
                      sendTo: newSendTo,
                      officeCodes: newSendTo === 'all' ? formData.officeCodes : [],
                      tahsilatGroups: newSendTo === 'tahsilat' ? formData.tahsilatGroups : [],
                    });
                  }}
                  simple
                />
              </div>

              {/* Tahsilat kategorileri (sadece sendTo === 'tahsilat' iken) */}
              {formData.sendTo === 'tahsilat' && (
                <div className="rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50/80 to-white overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-blue-100/80 bg-white/60">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                        <Target className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">Hedef kategoriler</p>
                        <p className="text-xs text-gray-500 truncate">En az bir tahsilat kategorisi seçin</p>
                      </div>
                    </div>
                    {formData.tahsilatGroups.length > 0 ? (
                      <span className="shrink-0 rounded-full bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white">
                        {formData.tahsilatGroups.length} seçili
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                        Zorunlu
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex flex-wrap gap-2">
                      {(tahsilatGroups || []).map((g) => {
                        const isSelected = formData.tahsilatGroups.includes(g.value);
                        return (
                          <button
                            key={g.value}
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                tahsilatGroups: isSelected
                                  ? formData.tahsilatGroups.filter((v) => v !== g.value)
                                  : [...formData.tahsilatGroups, g.value],
                              });
                            }}
                            className={`inline-flex items-center rounded-full border px-3.5 py-2 text-sm font-medium transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-500 text-white shadow-sm shadow-blue-200'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50/50'
                            }`}
                          >
                            {g.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Mağaza Filtresi (sadece sendTo === 'all' iken göster) */}
              {formData.sendTo === 'all' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mağaza Filtresi (Opsiyonel)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Seçili mağazalardan en az birinde hesabı olan kullanıcılara gönderilir. Boş bırakılırsa tüm kullanıcılara gider.
                  </p>
                  <div className="space-y-2 p-4 bg-gray-50 rounded-xl border border-gray-200 max-h-60 overflow-y-auto">
                    {STORE_LOCATIONS.map((store) => (
                      <label
                        key={store.code}
                        className="flex items-center gap-3 cursor-pointer group hover:bg-white p-2 rounded-lg transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.officeCodes.includes(store.code)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                officeCodes: [...formData.officeCodes, store.code],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                officeCodes: formData.officeCodes.filter((code) => code !== store.code),
                              });
                            }
                          }}
                          className="w-5 h-5 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex-1 flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {store.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({store.code})
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {formData.officeCodes.length > 0 && (
                    <p className="text-xs text-blue-600 mt-2 font-medium">
                      ✓ {formData.officeCodes.length} mağaza seçildi
                    </p>
                  )}
                </div>
              )}

              {/* Selected Users — sekmeli: Manuel TC / CSV */}
              {formData.sendTo === 'selected' && (
                <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                  <div className="flex p-1.5 gap-1 bg-gray-50 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => setRecipientInputMode('manual')}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        recipientInputMode === 'manual'
                          ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Tek tek TC ekle
                      {formData.userIds.length > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                          {formData.userIds.length}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRecipientInputMode('csv')}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        recipientInputMode === 'csv'
                          ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      CSV listesi
                      {recipientBatchId && (
                        <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                          ✓
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="p-5">
                    {recipientInputMode === 'manual' ? (
                      <div>
                        <div className="flex items-start gap-2 mb-3">
                          <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" aria-hidden />
                          <p className="text-xs text-gray-500 leading-relaxed">
                            Alıcı eklemek için 11 haneli TC kimlik numarası veya kullanıcı ID yazın ve Enter'a basın.
                            Birden fazla kayıt eklemek için virgülle ayırabilir veya listeyi doğrudan yapıştırabilirsiniz.
                          </p>
                        </div>
                        <div className="min-h-[44px] px-3 py-2 border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 flex flex-wrap items-center gap-2 bg-gray-50/50">
                          {formData.userIds.map((id, index) => (
                            <span
                              key={`${id}-${index}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm font-medium"
                            >
                              {id}
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    userIds: formData.userIds.filter((_, i) => i !== index),
                                  });
                                }}
                                className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Kaldır"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ))}
                          <input
                            type="text"
                            value={selectedUserInputValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSelectedUserInputValue(v);
                              const digitsOnly = v.replace(/\D/g, '');
                              if (digitsOnly.length === 11 && !formData.userIds.includes(digitsOnly)) {
                                setFormData({ ...formData, userIds: [...formData.userIds, digitsOnly] });
                                setSelectedUserInputValue('');
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                const trimmed = selectedUserInputValue.trim();
                                if (trimmed && !formData.userIds.includes(trimmed)) {
                                  setFormData({ ...formData, userIds: [...formData.userIds, trimmed] });
                                  setSelectedUserInputValue('');
                                }
                              }
                            }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const pasted = (e.clipboardData.getData('text') || '')
                                .split(/[\s,]+/)
                                .map((s) => s.trim())
                                .filter(Boolean);
                              const newIds = [...formData.userIds];
                              pasted.forEach((id) => {
                                if (id && !newIds.includes(id)) newIds.push(id);
                              });
                              if (newIds.length > formData.userIds.length) {
                                setFormData({ ...formData, userIds: newIds });
                                setSelectedUserInputValue('');
                              }
                            }}
                            className="flex-1 min-w-[140px] py-1 px-1 border-0 outline-none bg-transparent text-gray-900 placeholder-gray-400 text-sm"
                            placeholder={formData.userIds.length === 0 ? 'TC no (11 hane) veya ID girin' : 'Yeni ekle...'}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="rounded-xl border border-dashed border-blue-200 bg-blue-50/40 p-4">
                          <p className="text-sm font-medium text-gray-800 mb-3">Yeni CSV yükle</p>
                          <input
                            type="text"
                            value={csvName}
                            onChange={(e) => setCsvName(e.target.value)}
                            placeholder="Liste adı (opsiyonel)"
                            className="w-full px-3 py-2 mb-3 border border-gray-200 rounded-lg text-sm bg-white"
                          />
                          <div className="flex flex-col sm:flex-row gap-2">
                            <label className="flex-1 flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
                              <span className="shrink-0 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
                                Dosya seç
                              </span>
                              <span className="truncate">{csvFile ? csvFile.name : 'Dosya seçilmedi'}</span>
                              <input
                                ref={csvFileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                                className="hidden"
                              />
                            </label>
                            <button
                              type="button"
                              onClick={handleCsvUpload}
                              disabled={!csvFile || csvImportLoading}
                              className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
                            >
                              {csvImportLoading ? 'Yükleniyor...' : 'Yükle'}
                            </button>
                          </div>

                          {(recipientBatchId || csvFile || csvImportResult) && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-blue-100">
                              {recipientBatchId && (
                                <span className="text-xs px-2.5 py-1 rounded-full bg-white text-blue-700 border border-blue-200 font-medium">
                                  {selectedImportSummary?.validCount ?? 0} kullanıcı seçili
                                </span>
                              )}
                              {csvFile && !recipientBatchId && (
                                <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                                  {csvFile.name}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={clearCsvRecipientSelection}
                                className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
                              >
                                <X size={14} />
                                Temizle
                              </button>
                            </div>
                          )}

                          {csvImportResult?.summary && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                              <div className="text-xs bg-white rounded-lg px-2.5 py-2 border border-gray-100">
                                Toplam <span className="font-semibold block">{csvImportResult.summary.totalRows || 0}</span>
                              </div>
                              <div className="text-xs bg-white rounded-lg px-2.5 py-2 border border-gray-100">
                                Geçerli <span className="font-semibold text-green-600 block">{csvImportResult.summary.validCount || 0}</span>
                              </div>
                              <div className="text-xs bg-white rounded-lg px-2.5 py-2 border border-gray-100">
                                Geçersiz <span className="font-semibold text-red-600 block">{csvImportResult.summary.invalidCount || 0}</span>
                              </div>
                              <div className="text-xs bg-white rounded-lg px-2.5 py-2 border border-gray-100">
                                Tekrar <span className="font-semibold block">{csvImportResult.summary.duplicateCount || 0}</span>
                              </div>
                            </div>
                          )}

                          {Array.isArray(csvImportResult?.invalidRows) && csvImportResult.invalidRows.length > 0 && (
                            <div className="mt-3 rounded-lg bg-white border border-red-100 p-3">
                              <p className="text-xs font-medium text-gray-700 mb-1">Hatalı satırlar (ilk 10)</p>
                              <div className="max-h-24 overflow-auto space-y-0.5">
                                {csvImportResult.invalidRows.slice(0, 10).map((row, idx) => (
                                  <p key={`${row?.row || idx}-${idx}`} className="text-xs text-red-600">
                                    Satır {row?.row || '-'}: {row?.value || '-'} ({row?.reason || 'Hata'})
                                  </p>
                                ))}
                              </div>
                              {csvImportResult?.errorFileUrl && (
                                <a
                                  href={csvImportResult.errorFileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-block mt-2 text-xs text-blue-600 hover:underline"
                                >
                                  Hataları indir
                                </a>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <p className="text-sm font-medium text-gray-800">Kayıtlı CSV listeleri</p>
                            <label className="inline-flex items-center gap-2 text-xs text-gray-500 shrink-0">
                              <input
                                type="checkbox"
                                checked={includeArchived}
                                onChange={(e) => {
                                  const next = e.target.checked;
                                  setIncludeArchived(next);
                                  fetchRecipientImports(importsQuery, next);
                                }}
                                className="w-4 h-4 rounded"
                              />
                              Arşiv
                            </label>
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={importsQuery}
                              onChange={(e) => setImportsQuery(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && fetchRecipientImports(importsQuery)}
                              placeholder="Liste ara..."
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                            />
                            <button
                              type="button"
                              onClick={() => fetchRecipientImports(importsQuery)}
                              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-100"
                            >
                              Ara
                            </button>
                          </div>

                          <div className="mt-3 max-h-40 overflow-auto space-y-2">
                            {importsLoading ? (
                              <p className="text-xs text-gray-500 py-2">Yükleniyor...</p>
                            ) : imports.length === 0 ? (
                              <p className="text-xs text-gray-500 py-2">Kayıtlı liste bulunamadı.</p>
                            ) : (
                              imports.map((item) => (
                                <div
                                  key={item.batchId}
                                  className={`rounded-lg border p-3 bg-white ${
                                    recipientBatchId === item.batchId
                                      ? 'border-blue-300 ring-1 ring-blue-200'
                                      : 'border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {item.name || item.fileName || item.batchId}
                                        </p>
                                        {isImportArchived(item) && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                            Arşiv
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        {item.validCount || 0} geçerli · {item.invalidCount || 0} geçersiz
                                      </p>
                                    </div>
                                    <div className="flex gap-1.5 shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => handleSelectImport(item.batchId)}
                                        className="px-3 py-1.5 text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                      >
                                        Seç
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingImportId(item.batchId);
                                          setEditingImportName(item.name || '');
                                        }}
                                        className="px-2 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                                        title="Ad düzenle"
                                      >
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          isImportArchived(item)
                                            ? handleUnarchiveImport(item.batchId)
                                            : handleArchiveImport(item.batchId)
                                        }
                                        className="px-2 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                                        title={isImportArchived(item) ? 'Arşivden çıkar' : 'Arşivle'}
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                  {editingImportId === item.batchId && (
                                    <div className="mt-2 flex gap-2">
                                      <input
                                        type="text"
                                        value={editingImportName}
                                        onChange={(e) => setEditingImportName(e.target.value)}
                                        className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                                        placeholder="Yeni liste adı"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateImportName(item.batchId)}
                                        className="px-3 py-1.5 text-xs bg-green-500 text-white rounded-lg hover:bg-green-600"
                                      >
                                        Kaydet
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingImportId('');
                                          setEditingImportName('');
                                        }}
                                        className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50"
                                      >
                                        İptal
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tetiklenme zamanı sekmelerden seçildi - formData.sendType aktif sekme ile belirlenir */}

              {/* Scheduled Time - Bildirim sekmesinde (immediate) tek seferlik zamanlanmış için */}
              {formData.sendType === 'immediate' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zamanlama (Opsiyonel)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Boş bırakırsanız bildirim hemen gönderilir. Tarih ve saat seçerseniz tek seferlik zamanlanmış bildirim olur.
                  </p>
                  <input
                    type="datetime-local"
                    value={
                      formData.scheduledTime
                        ? (() => {
                          // ISO8601 veya "YYYY-MM-DD HH:mm:ss" formatından datetime-local formatına çevir
                          let date;
                          if (formData.scheduledTime.includes('T')) {
                            // ISO8601 formatı ise (örn: 2026-02-17T14:01:00+03:00 veya 2026-02-17T11:01:00.000Z)
                            date = new Date(formData.scheduledTime);
                          } else {
                            // "YYYY-MM-DD HH:mm:ss" formatı ise (eski format desteği)
                            const dateTimeMatch = formData.scheduledTime.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
                            if (dateTimeMatch) {
                              const [, year, month, day, hours, minutes] = dateTimeMatch;
                              date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
                            } else {
                              date = new Date(formData.scheduledTime);
                            }
                          }
                          // Yıl, ay, gün, saat, dakika bilgilerini al (local timezone)
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          // datetime-local formatı: YYYY-MM-DDTHH:mm
                          return `${year}-${month}-${day}T${hours}:${minutes}`;
                        })()
                        : ''
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        // datetime-local değerini local timezone'da "YYYY-MM-DD HH:mm:ss" formatına çevir
                        // datetime-local formatı: YYYY-MM-DDTHH:mm (local timezone)
                        const localDateTime = e.target.value;

                        // Local timezone'da Date objesi oluştur
                        // "2025-01-17T10:05" formatındaki string'i parse et
                        const [datePart, timePart] = localDateTime.split('T');
                        const [year, month, day] = datePart.split('-').map(Number);
                        const [hours, minutes] = timePart.split(':').map(Number);

                        // Local timezone'da Date objesi oluştur
                        const localDate = new Date(year, month - 1, day, hours, minutes);

                        // Local timezone'da ISO8601 formatına çevir (timezone offset ile)
                        const formattedYear = localDate.getFullYear();
                        const formattedMonth = String(localDate.getMonth() + 1).padStart(2, '0');
                        const formattedDay = String(localDate.getDate()).padStart(2, '0');
                        const formattedHours = String(localDate.getHours()).padStart(2, '0');
                        const formattedMinutes = String(localDate.getMinutes()).padStart(2, '0');
                        const formattedSeconds = String(localDate.getSeconds()).padStart(2, '0');
                        
                        // Timezone offset'i al (dakika cinsinden, negatif = UTC+)
                        const timezoneOffset = -localDate.getTimezoneOffset(); // Negatif çünkü getTimezoneOffset() ters döner
                        const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
                        const offsetMinutes = Math.abs(timezoneOffset) % 60;
                        const offsetSign = timezoneOffset >= 0 ? '+' : '-';
                        const offsetString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
                        
                        // ISO8601 formatı: YYYY-MM-DDTHH:mm:ss+HH:mm
                        const formattedTime = `${formattedYear}-${formattedMonth}-${formattedDay}T${formattedHours}:${formattedMinutes}:${formattedSeconds}${offsetString}`;

                        setHighlightScheduledTime(false);
                        setFormData({
                          ...formData,
                          scheduledTime: formattedTime,
                        });
                      } else {
                        setHighlightScheduledTime(false);
                        setFormData({
                          ...formData,
                          scheduledTime: null,
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Seçtiğiniz tarih ve saat yerel saat diliminize göre kaydedilecektir.
                  </p>
                </div>
              )}

              {/* Scheduled Time - Sadece scheduled seçildiğinde göster */}
              {formData.sendType === 'scheduled' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zamanlama Tarihi ve Saati *
                  </label>
                  <input
                    type="datetime-local"
                    value={
                      formData.scheduledTime
                        ? (() => {
                          // ISO8601 veya "YYYY-MM-DD HH:mm:ss" formatından datetime-local formatına çevir
                          let date;
                          if (formData.scheduledTime.includes('T')) {
                            // ISO8601 formatı ise (örn: 2026-02-17T14:01:00+03:00 veya 2026-02-17T11:01:00.000Z)
                            date = new Date(formData.scheduledTime);
                          } else {
                            // "YYYY-MM-DD HH:mm:ss" formatı ise (eski format desteği)
                            const dateTimeMatch = formData.scheduledTime.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
                            if (dateTimeMatch) {
                              const [, year, month, day, hours, minutes] = dateTimeMatch;
                              date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
                            } else {
                              date = new Date(formData.scheduledTime);
                            }
                          }
                          // Yıl, ay, gün, saat, dakika bilgilerini al (local timezone)
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          // datetime-local formatı: YYYY-MM-DDTHH:mm
                          return `${year}-${month}-${day}T${hours}:${minutes}`;
                        })()
                        : ''
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        // datetime-local değerini local timezone'da "YYYY-MM-DD HH:mm:ss" formatına çevir
                        // datetime-local formatı: YYYY-MM-DDTHH:mm (local timezone)
                        const localDateTime = e.target.value;

                        // Local timezone'da Date objesi oluştur
                        // "2025-01-17T10:05" formatındaki string'i parse et
                        const [datePart, timePart] = localDateTime.split('T');
                        const [year, month, day] = datePart.split('-').map(Number);
                        const [hours, minutes] = timePart.split(':').map(Number);

                        // Local timezone'da Date objesi oluştur
                        const localDate = new Date(year, month - 1, day, hours, minutes);

                        // Local timezone'da ISO8601 formatına çevir (timezone offset ile)
                        const formattedYear = localDate.getFullYear();
                        const formattedMonth = String(localDate.getMonth() + 1).padStart(2, '0');
                        const formattedDay = String(localDate.getDate()).padStart(2, '0');
                        const formattedHours = String(localDate.getHours()).padStart(2, '0');
                        const formattedMinutes = String(localDate.getMinutes()).padStart(2, '0');
                        const formattedSeconds = String(localDate.getSeconds()).padStart(2, '0');
                        
                        // Timezone offset'i al (dakika cinsinden, negatif = UTC+)
                        const timezoneOffset = -localDate.getTimezoneOffset(); // Negatif çünkü getTimezoneOffset() ters döner
                        const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
                        const offsetMinutes = Math.abs(timezoneOffset) % 60;
                        const offsetSign = timezoneOffset >= 0 ? '+' : '-';
                        const offsetString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
                        
                        // ISO8601 formatı: YYYY-MM-DDTHH:mm:ss+HH:mm
                        const formattedTime = `${formattedYear}-${formattedMonth}-${formattedDay}T${formattedHours}:${formattedMinutes}:${formattedSeconds}${offsetString}`;

                        setHighlightScheduledTime(false);
                        setFormData({
                          ...formData,
                          scheduledTime: formattedTime,
                        });
                      } else {
                        setHighlightScheduledTime(false);
                        setFormData({
                          ...formData,
                          scheduledTime: null,
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${
                      highlightScheduledTime
                        ? 'border-red-400 ring-2 ring-red-200 focus:ring-red-400'
                        : 'border-gray-200 focus:ring-blue-500'
                    }`}
                  />
                  {highlightScheduledTime && (
                    <p className="text-xs text-red-600 mt-1">
                      Scheduled durumunda tarih-saat alanı zorunludur.
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Seçtiğiniz tarih ve saat yerel saat diliminize göre kaydedilecektir.
                  </p>
                </div>
              )}

              {/* Recurring Options - Sadece recurring seçildiğinde göster */}
              {formData.sendType === 'recurring' && (
                <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                      <Calendar className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Tekrarlama planı</p>
                      <p className="text-xs text-gray-500">Bildirimin ne sıklıkla gönderileceğini belirleyin</p>
                    </div>
                  </div>
                  <div className="space-y-4 p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                        Tekrarlama sıklığı *
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'daily', label: 'Her gün' },
                          { value: 'weekly', label: 'Her hafta' },
                          { value: 'monthly', label: 'Her ay' },
                        ].map((option) => {
                          const isActive = formData.recurringType === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  recurringType: option.value,
                                  recurringTime: formData.recurringTime,
                                  recurringDayOfWeek: option.value === 'weekly' ? (formData.recurringDayOfWeek ?? 1) : null,
                                  recurringDayOfMonth: option.value === 'monthly' ? (formData.recurringDayOfMonth ?? 1) : null,
                                  scheduledTime: null,
                                });
                              }}
                              className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition-all ${
                                isActive
                                  ? 'border-violet-500 bg-violet-500 text-white shadow-sm shadow-violet-200'
                                  : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-violet-300 hover:bg-violet-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {formData.recurringType ? (
                      <div className="space-y-4 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                        {formData.recurringType === 'weekly' && (
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                              Haftanın günü *
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {[
                                { value: 1, label: 'Pzt' },
                                { value: 2, label: 'Sal' },
                                { value: 3, label: 'Çar' },
                                { value: 4, label: 'Per' },
                                { value: 5, label: 'Cum' },
                                { value: 6, label: 'Cmt' },
                                { value: 0, label: 'Paz' },
                              ].map((day) => {
                                const isActive = (formData.recurringDayOfWeek ?? 1) === day.value;
                                return (
                                  <button
                                    key={day.value}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, recurringDayOfWeek: day.value })}
                                    className={`min-w-[2.75rem] rounded-lg border px-2.5 py-2 text-xs font-semibold transition-all ${
                                      isActive
                                        ? 'border-violet-500 bg-violet-500 text-white'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-violet-300'
                                    }`}
                                  >
                                    {day.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {formData.recurringType === 'monthly' && (
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                              Ayın günü *
                            </p>
                            <Dropdown
                              items={[
                                { value: '-1', label: 'Ayın son günü' },
                                ...Array.from({ length: 31 }, (_, i) => ({
                                  value: String(i + 1),
                                  label: `${i + 1}. gün`,
                                })),
                              ]}
                              value={String(formData.recurringDayOfMonth ?? 1)}
                              onChange={(v) => {
                                const value = v === '-1' ? -1 : parseInt(v);
                                setFormData({ ...formData, recurringDayOfMonth: value });
                              }}
                              simple
                            />
                          </div>
                        )}

                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                            Gönderim saati *
                          </p>
                          <input
                            type="time"
                            value={formData.recurringTime || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                recurringTime: e.target.value,
                              })
                            }
                            className="w-full max-w-xs px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            Bildirim seçtiğiniz periyotta bu saatte gönderilir.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 px-1">
                        Devam etmek için yukarıdan bir tekrarlama sıklığı seçin.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Event-Based Options - Sadece event_based seçildiğinde göster */}
              {formData.sendType === 'event_based' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Etkinlik Tipi *
                    </label>
                    <Dropdown
                      items={[
                        { value: '', label: eventTypesLoading ? 'Yükleniyor...' : 'Seçiniz' },
                        ...eventTypes.map((et) => ({ value: et.value, label: et.label })),
                      ]}
                      value={formData.eventType || ''}
                      onChange={(val) => {
                        const selectedEventType = eventTypes.find((et) => et.value === val);
                        setFormData({
                          ...formData,
                          eventType: val,
                          title: selectedEventType?.defaultTitle || formData.title,
                          body: selectedEventType?.defaultBody || formData.body,
                        });
                      }}
                      placeholder={eventTypesLoading ? 'Yükleniyor...' : 'Seçiniz'}
                      simple
                      disabled={eventTypesLoading}
                    />
                    {formData.eventType && (() => {
                      const selectedEventType = eventTypes.find(et => et.value === formData.eventType);
                      return selectedEventType ? (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs text-gray-600">
                            <span className="font-medium">Açıklama:</span> {selectedEventType.description}
                          </p>
                        </div>
                      ) : null;
                    })()}
                    <p className="text-xs text-gray-500 mt-2">
                      Seçilen etkinlik gerçekleştiğinde bildirim otomatik olarak gönderilecektir.
                    </p>
                  </div>

                  {formData.eventType === 'inactive_since_first_open' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-xs font-medium text-amber-800 mb-2">
                        Pasif ilk giriş kampanyası şablonları
                      </p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {inactiveSinceFirstOpenPresets.map((preset) => {
                          const isActive = isEventPresetActive(preset, formData);
                          return (
                          <button
                            key={preset.key}
                            type="button"
                            onClick={() => {
                              setFormData((prev) => applyEventPreset(prev, preset));
                              toast.success(`${preset.label} uygulandı`);
                            }}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                              isActive
                                ? 'border-amber-500 text-amber-900 bg-amber-200 ring-2 ring-amber-300/60'
                                : 'border-amber-300 text-amber-800 bg-white hover:bg-amber-100'
                            }`}
                          >
                            {preset.label}
                          </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {formData.eventType === 'payment_reminder' && (
                    <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl">
                      <p className="text-xs font-medium text-sky-800 mb-2">
                        Ödeme hatırlatma senaryoları
                      </p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {paymentReminderPresets.map((preset) => {
                          const isActive = isEventPresetActive(preset, formData);
                          return (
                            <button
                              key={preset.key}
                              type="button"
                              onClick={() => {
                                setFormData((prev) => applyEventPreset(prev, preset));
                                toast.success(`${preset.label} senaryosu uygulandı`);
                              }}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                                isActive
                                  ? 'border-sky-600 text-sky-900 bg-sky-200 ring-2 ring-sky-300/60'
                                  : 'border-sky-300 text-sky-800 bg-white hover:bg-sky-100'
                              }`}
                            >
                              {preset.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Event-based bildirimler için gönderim saati - user_registered hariç */}
                  {formData.eventType !== 'user_registered' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gönderim Saati (Opsiyonel)
                      </label>
                      <input
                        type="time"
                        value={formData.recurringTime || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            recurringTime: e.target.value, // HH:mm formatında (örn: "14:30")
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Belirtilirse, bildirim bu saatte (HH:mm formatında, örn: 14:30) gönderilir. Boş bırakılırsa varsayılan saat kullanılır. Her dakika çalışan cron job bu saati kontrol eder.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* 6. Bildirim Kategorisi - Kanallar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bildirim Kategorisi *
                </label>
                <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.channels.includes('push')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            channels: [...formData.channels, 'push'],
                          });
                        } else {
                          const newChannels = formData.channels.filter((c) => c !== 'push');
                          // En az bir kanal seçili olmalı
                          if (newChannels.length === 0) {
                            toast.error('En az bir kanal seçilmelidir');
                            return;
                          }
                          setFormData({
                            ...formData,
                            channels: newChannels,
                          });
                        }
                      }}
                      className="w-5 h-5 text-blue-500 rounded mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 block">
                        📱 Push Notification
                      </span>
                      <span className="text-xs text-gray-500 block mt-1">
                        Uygulama kapalıyken de bildirim gelir. Telefon bildirim çubuğunda görünür.
                      </span>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.channels.includes('websocket')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            channels: [...formData.channels, 'websocket'],
                          });
                        } else {
                          const newChannels = formData.channels.filter((c) => c !== 'websocket');
                          // En az bir kanal seçili olmalı
                          if (newChannels.length === 0) {
                            toast.error('En az bir kanal seçilmelidir');
                            return;
                          }
                          setFormData({
                            ...formData,
                            channels: newChannels,
                          });
                        }
                      }}
                      className="w-5 h-5 text-blue-500 rounded mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 block">
                        🔔 WebSocket
                      </span>
                      <span className="text-xs text-gray-500 block mt-1">
                        Uygulama açıkken anlık bildirim. WebSocket ile gönderilir.
                      </span>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.channels.includes('email')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            channels: [...formData.channels, 'email'],
                          });
                        } else {
                          const newChannels = formData.channels.filter((c) => c !== 'email');
                          // En az bir kanal seçili olmalı
                          if (newChannels.length === 0) {
                            toast.error('En az bir kanal seçilmelidir');
                            return;
                          }
                          setFormData({
                            ...formData,
                            channels: newChannels,
                          });
                        }
                      }}
                      className="w-5 h-5 text-blue-500 rounded mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 block">
                        📧 E-posta
                      </span>
                      <span className="text-xs text-gray-500 block mt-1">
                        Kullanıcının e-posta adresine bildirim gönderilir.
                      </span>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.channels.includes('sms')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            channels: [...formData.channels, 'sms'],
                          });
                        } else {
                          const newChannels = formData.channels.filter((c) => c !== 'sms');
                          // En az bir kanal seçili olmalı
                          if (newChannels.length === 0) {
                            toast.error('En az bir kanal seçilmelidir');
                            return;
                          }
                          setFormData({
                            ...formData,
                            channels: newChannels,
                          });
                        }
                      }}
                      className="w-5 h-5 text-blue-500 rounded mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 block">
                        💬 SMS
                      </span>
                      <span className="text-xs text-gray-500 block mt-1">
                        Kullanıcının telefon numarasına SMS bildirimi gönderilir.
                      </span>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Önerilen: Hem Push hem de Uygulama İçi seçili olmalıdır. Böylece bildirim hem kapalı hem açık uygulamada görünür.
                </p>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Öncelik
                </label>
                <Dropdown
                  items={[
                    { value: 'normal', label: 'Normal' },
                    { value: 'high', label: 'Yüksek' },
                  ]}
                  value={formData.priority}
                  onChange={(v) => setFormData({ ...formData, priority: v })}
                  simple
                />
              </div>

              {/* Promotion Data - Sadece promotion type seçildiğinde göster */}
              {formData.type === 'promotion' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Promosyon Bilgileri (Opsiyonel)
                  </label>
                  <div className="space-y-3 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Promosyon ID
                      </label>
                      <input
                        type="text"
                        value={formData.promotionData?.id || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            promotionData: {
                              ...formData.promotionData,
                              id: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="promo123"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Açıklama
                      </label>
                      <textarea
                        rows="2"
                        value={formData.promotionData?.description || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            promotionData: {
                              ...formData.promotionData,
                              description: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Detaylı açıklama"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Geçerlilik Tarihi (Opsiyonel)
                      </label>
                      <input
                        type="datetime-local"
                        value={
                          formData.promotionData?.validUntil
                            ? new Date(formData.promotionData.validUntil)
                              .toISOString()
                              .slice(0, 16)
                            : ''
                        }
                        onChange={(e) => {
                          if (e.target.value) {
                            const [datePart, timePart] = e.target.value.split('T');
                            const [year, month, day] = datePart.split('-').map(Number);
                            const [hours, minutes] = timePart.split(':').map(Number);
                            const localDate = new Date(year, month - 1, day, hours, minutes);
                            setFormData({
                              ...formData,
                              promotionData: {
                                ...formData.promotionData,
                                validUntil: localDate.toISOString(),
                              },
                            });
                          } else {
                            setFormData({
                              ...formData,
                              promotionData: {
                                ...formData.promotionData,
                                validUntil: null,
                              },
                            });
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                İptal
              </button>
              <button
                onClick={showEditModal ? handleUpdate : handleCreate}
                disabled={
                  loading ||
                  !formData.title ||
                  !formData.body ||
                  !formData.channels ||
                  formData.channels.length === 0 ||
                  (formData.sendTo === 'selected' && (!formData.userIds || formData.userIds.length === 0) && !recipientBatchId)
                }
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Kaydediliyor...' : showEditModal ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Duplicate CSV Name Warning Modal */}
      {duplicateCsvWarning.open && createPortal((
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-xl w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Aynı isimde CSV bulundu</h3>
            <p className="text-gray-600 mb-4">
              Bu isimde bir CSV listesi zaten mevcut. Yanlışlıkla tekrar yüklüyor olabilirsiniz.
            </p>
            {duplicateCsvWarning.existing && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm text-amber-900">
                <p>
                  <span className="font-medium">Mevcut kayıt:</span>{' '}
                  {duplicateCsvWarning.existing.name || duplicateCsvWarning.existing.fileName || '-'}
                </p>
                <p>
                  <span className="font-medium">Oluşturulma:</span>{' '}
                  {duplicateCsvWarning.existing.createdAt
                    ? new Date(duplicateCsvWarning.existing.createdAt).toLocaleString('tr-TR')
                    : '-'}
                </p>
                <p>
                  <span className="font-medium">Geçerli kullanıcı:</span>{' '}
                  {duplicateCsvWarning.existing.validCount ?? 0}
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDuplicateCsvWarning({ open: false, name: '', existing: null })}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={() => performCsvUpload(true)}
                disabled={csvImportLoading}
                className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Yine de Yükle
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && createPortal((
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-xl w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {deleteConfirm.deleteInfo?.modalTitle || 'Bildirimi Sil'}
            </h3>
            <p className="text-gray-600 mb-4">
              {deleteConfirm.deleteInfo?.modalDescription ? (
                deleteConfirm.deleteInfo.modalDescription
              ) : (
                <>
                  <span className="font-semibold">{deleteConfirm.notification?.title || 'Bu bildirim'}</span> adlı bildirimi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
                </>
              )}
            </p>
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="text-sm space-y-1">
                <p><span className="font-medium">Başlık:</span> {deleteConfirm.notification?.title || '-'}</p>
                <p><span className="font-medium">Durum:</span> {deleteConfirm.notification?.status ? getStatusLabel(deleteConfirm.notification.status) : '-'}</p>
                {deleteConfirm.notification?.recurringType && (
                  <p><span className="font-medium">Tekrarlama:</span> {formatRecurringInfo(deleteConfirm.notification) || deleteConfirm.notification.recurringType}</p>
                )}
                {deleteConfirm.notification?.scheduledTime && (
                  <p><span className="font-medium">Zamanlanma:</span> {deleteConfirm.notification.scheduledTime}</p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, notification: null, deleteInfo: null })}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Vazgeç
              </button>
              <button
                onClick={() => { void handleDelete(deleteConfirm.notification); }}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Siliniyor...
                  </>
                ) : (
                  deleteConfirm.deleteInfo?.confirmLabel || 'Sil'
                )}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export default NotificationManagement;
